import { BACKEND_URL } from "../config";
import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import BreakoutAlerts from "./BreakoutAlerts";
import InteractiveTradingChart from "./InteractiveTradingChart";
import { 
  isPushSupported, 
  subscribeToPush, 
  unsubscribeFromPush,
  isSubscribed,
  requestNotificationPermission,
  getNotificationPermission 
} from "../utils/pushNotifications";

const API = `${BACKEND_URL}/api`;

const FOREX_PAIRS = [
  // Existing Major Forex
  "EURUSD",
  "GBPUSD",
  "USDJPY",
  "USDCHF",
  "AUDUSD",
  "USDCAD",
  "NZDUSD",
  "EURAUD",
  "EURGBP",
  // High Priority - Forex Majors
  "EURCHF",
  "GBPJPY",
  "EURJPY",
  "AUDJPY",
  "GBPAUD",
  // Medium Priority - Forex
  "NZDJPY",
  "CADCHF",
  "CHFJPY",
  "CADJPY",
  "AUDNZD",  // Added Feb 18, 2026
  // Added May 1, 2026 Sowing Day T2 — revives WIDE_RANGE_DRIFT + feeds shadow harvest
  "GBPCAD",
  "GBPNZD"
];

const CRYPTO_PAIRS = [
  // Existing Crypto
  "BTCUSD",
  "ETHUSD",
  // High Priority - Crypto
  "SOLUSD",
  "XRPUSD",
  "BNBUSD",
  "ADAUSD"
];

const CURRENCY_PAIRS = [...FOREX_PAIRS, ...CRYPTO_PAIRS];

const ForexCalculator = ({ mt5Connected, mt5Account, onLogout }) => {
  const [selectedPair, setSelectedPair] = useState("");
  const [riskAmount, setRiskAmount] = useState("");
  const [accountBalance, setAccountBalance] = useState("");
  const [enableAutoSL, setEnableAutoSL] = useState(false);
  const [profitAlertPercent, setProfitAlertPercent] = useState(1.0);
  const [timeframe, setTimeframe] = useState("1D"); // Default to Daily
  const [trailingStops, setTrailingStops] = useState([
    { profit_target_percent: 50, move_sl_to_percent: 10 }
  ]);
  // Partial Take Profit state
  const [enablePartialTP, setEnablePartialTP] = useState(false);
  const [partialTPLevels, setPartialTPLevels] = useState([
    { percent: 50, ratio: 1.5 },
    { percent: 30, ratio: 2.0 }
  ]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showTradeModal, setShowTradeModal] = useState(false);
  const [volumeAnalysis, setVolumeAnalysis] = useState(null);
  const [loadingVolume, setLoadingVolume] = useState(false);
  const [tradeId, setTradeId] = useState(null);
  const [monitoringActive, setMonitoringActive] = useState(false);
  
  // New states for interactive chart flow
  const [showChart, setShowChart] = useState(false);
  const [calculatedResult, setCalculatedResult] = useState(null);
  
  // Pair category tab state
  const [pairCategory, setPairCategory] = useState('forex'); // 'forex' or 'crypto'
  
  // Subscription modal states
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [subscriptionModalMessage, setSubscriptionModalMessage] = useState("");

  // Subscription status for MT5 feature lock
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);

  // Session warning state
  const [sessionWarning, setSessionWarning] = useState(null);
  
  // Calculator disabled state (during rollover period)
  const [calculatorDisabled, setCalculatorDisabled] = useState(false);
  
  // Breakout alerts state
  const [breakoutAlerts, setBreakoutAlerts] = useState([]);
  
  // 🎯 NEW: Trade Opportunity Scanner state
  const [tradeOpportunities, setTradeOpportunities] = useState([]);
  const [opportunitiesLoading, setOpportunitiesLoading] = useState(false);
  const [lastOpportunityCount, setLastOpportunityCount] = useState(0);
  
  // 🎯 NEW: Selected direction from opportunity (auto-fills when clicking opportunity)
  const [selectedDirection, setSelectedDirection] = useState(null); // "BUY" or "SELL" or null
  
  // 🎯 NEW: Track active trades per pair for pulsating buttons
  const [activeTradePairs, setActiveTradePairs] = useState({}); // { "EURUSD": { trade_id: "xxx", direction: "BUY" }, ... }
  
  // 🎯 NEW: Current time for opportunity timers (updates every second)
  const [currentTime, setCurrentTime] = useState(new Date());
  
  // 🔔 Push Notification State
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  const [pushSupported, setPushSupported] = useState(false);
  
  // 🎯 Trading Mode State (SCALP or SWING)
  const [tradingMode, setTradingMode] = useState("SWING");
  const [tradingModeLoading, setTradingModeLoading] = useState(false);
  
  // 🌍 Macro Environment State
  const [macroEnvironment, setMacroEnvironment] = useState(null);
  
  const [bellAudioRef] = useState(() => {
    // Create audio element for bell chime notification
    const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1cW2RsdHuDioyMi4mFgXx3cW1oZGFgYGJkaG1ydnuAhIiKi4uKiIWCfnl0b2pmYmBfYGFjZmltcHN3e36ChYeIiIeFg4B9endzb2xpZmRjYmJjZGZpbG9zd3p9gIKFh4iIh4aEgX56d3Rxbmtoa');
    audio.volume = 0.5;
    return audio;
  });
  
  // Trading status for news warnings
  const [tradingStatus, setTradingStatus] = useState({
    calculator_enabled: true,
    block_due_to_news: false,
    news_recommendation: "",
    news_note: ""
  });

  // London Session countdown (Asian session warning)
  const [londonCountdown, setLondonCountdown] = useState({
    isAsianSession: false,
    hoursUntilLondon: 0,
    minutesUntilLondon: 0,
    secondsUntilLondon: 0,
    currentSession: ""
  });

  // NEW: States for collapsible sections
  const [expandedSections, setExpandedSections] = useState({
    technical: false,
    volume: false,
    currencyStrength: false,
    timeframeAlignment: false,
    partialTP: false,
    priceAction: false
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // ⏱️ CANDLE TIMING HELPER - Determines if it's safe to enter trades
  const getCandleTiming = () => {
    const now = new Date();
    const minutesIntoHour = now.getMinutes();
    
    // Define timing zones
    const isNewCandle = minutesIntoHour < 15;        // First 15 minutes - risky
    const isOptimalWindow = minutesIntoHour >= 15 && minutesIntoHour <= 50;  // 15-50 min - safe
    const isHourEnding = minutesIntoHour > 50;       // Last 10 minutes - risky
    
    let status, color, warning, emoji;
    
    if (isNewCandle) {
      const waitMinutes = 15 - minutesIntoHour;
      status = `Wait ${waitMinutes}m`;
      color = '#f59e0b';  // Orange/amber
      warning = `⚠️ NEW CANDLE - Wait ${waitMinutes} min for confirmation`;
      emoji = '⏳';
    } else if (isHourEnding) {
      const minutesLeft = 60 - minutesIntoHour;
      status = `${minutesLeft}m left`;
      color = '#ef4444';  // Red
      warning = `⚠️ HOUR ENDING - New candle in ${minutesLeft} min`;
      emoji = '⏰';
    } else {
      status = `${minutesIntoHour}m`;
      color = '#22c55e';  // Green
      warning = null;
      emoji = '✅';
    }
    
    return {
      minutesIntoHour,
      isNewCandle,
      isOptimalWindow,
      isHourEnding,
      isRisky: isNewCandle || isHourEnding,
      status,
      color,
      warning,
      emoji
    };
  };
  
  const candleTiming = getCandleTiming();

  // Fetch subscription info on component mount
  useEffect(() => {
    const fetchSubscriptionInfo = async () => {
      try {
        const response = await axios.get(`${API}/business/subscription/info`);
        setSubscriptionInfo(response.data);
      } catch (err) {
        console.error("Failed to fetch subscription info:", err);
      }
    };
    fetchSubscriptionInfo();
  }, []);

  // Fetch trading status for news warnings
  useEffect(() => {
    const fetchTradingStatus = async () => {
      try {
        const response = await axios.get(`${API}/trading-status`);
        if (response.data) {
          setTradingStatus({
            calculator_enabled: response.data.calculator_enabled ?? true,
            block_due_to_news: response.data.block_due_to_news ?? false,
            news_recommendation: response.data.news_recommendation || "",
            news_note: response.data.news_note || ""
          });
        }
      } catch (err) {
        console.error("Failed to fetch trading status:", err);
      }
    };
    
    // Fetch immediately
    fetchTradingStatus();
    
    // Refresh every 2 minutes
    const interval = setInterval(fetchTradingStatus, 120000);
    return () => clearInterval(interval);
  }, []);

  // 🌍 London Session Countdown - Warn during Asian session (low liquidity)
  // London opens at 08:00 UTC, closes at 16:00 UTC
  // Asian session (low liquidity): ~22:00 UTC to 07:00 UTC
  useEffect(() => {
    const checkLondonCountdown = () => {
      const now = new Date();
      const utcHour = now.getUTCHours();
      const utcMinutes = now.getUTCMinutes();
      const utcSeconds = now.getUTCSeconds();
      
      // Define session times (UTC)
      const londonOpen = 8;   // 08:00 UTC
      const londonClose = 16; // 16:00 UTC
      const asianStart = 22;  // 22:00 UTC (after NY close)
      const asianEnd = 7;     // 07:00 UTC (before London)
      
      let isAsianSession = false;
      let currentSession = "";
      let hoursUntilLondon = 0;
      let minutesUntilLondon = 0;
      let secondsUntilLondon = 0;
      
      // Determine current session
      if (utcHour >= londonOpen && utcHour < londonClose) {
        currentSession = "London";
        isAsianSession = false;
      } else if (utcHour >= 13 && utcHour < 22) {
        currentSession = "New York";
        isAsianSession = false;
      } else if (utcHour >= asianStart || utcHour < asianEnd) {
        currentSession = "Asian";
        isAsianSession = true;
        
        // Calculate time until London opens (08:00 UTC)
        let targetHour = londonOpen;
        let hoursToGo = 0;
        
        if (utcHour >= asianStart) {
          // After 22:00 UTC - need to wait until next day 08:00
          hoursToGo = (24 - utcHour) + targetHour;
        } else {
          // Before 08:00 UTC - same day
          hoursToGo = targetHour - utcHour;
        }
        
        // Adjust for minutes and seconds
        if (utcMinutes > 0 || utcSeconds > 0) {
          hoursToGo -= 1;
        }
        
        hoursUntilLondon = hoursToGo;
        minutesUntilLondon = 59 - utcMinutes;
        secondsUntilLondon = 59 - utcSeconds;
        
        // Handle edge case when exactly on the hour
        if (utcMinutes === 0 && utcSeconds === 0) {
          hoursUntilLondon += 1;
          minutesUntilLondon = 0;
          secondsUntilLondon = 0;
        }
      } else if (utcHour >= asianEnd && utcHour < londonOpen) {
        currentSession = "Pre-London";
        isAsianSession = true;
        
        // Calculate time until London opens
        hoursUntilLondon = londonOpen - utcHour - 1;
        minutesUntilLondon = 59 - utcMinutes;
        secondsUntilLondon = 59 - utcSeconds;
        
        if (utcMinutes === 0 && utcSeconds === 0) {
          hoursUntilLondon += 1;
          minutesUntilLondon = 0;
          secondsUntilLondon = 0;
        }
      }
      
      setLondonCountdown({
        isAsianSession,
        hoursUntilLondon,
        minutesUntilLondon,
        secondsUntilLondon,
        currentSession
      });
    };
    
    // Check immediately
    checkLondonCountdown();
    
    // Update every second for smooth countdown
    const interval = setInterval(checkLondonCountdown, 1000);
    return () => clearInterval(interval);
  }, []);

  // Check for daily rollover period warning (5:00 PM EST / 22:00 UTC)
  // DISABLE CALCULATOR during 2-hour window to protect accounts and data
  useEffect(() => {
    const checkRolloverWarning = () => {
      const now = new Date();
      const utcHour = now.getUTCHours();
      const utcMinute = now.getUTCMinutes();
      const currentMinutes = utcHour * 60 + utcMinute;

      // Daily Forex Rollover/Settlement: 5:00 PM EST = 22:00 UTC (10:00 PM UTC)
      const rolloverStartMinutes = 22 * 60 + 0; // 22:00 UTC
      const rolloverEndMinutes = 22 * 60 + 5;   // 22:05 UTC (5-minute closure)

      const minutesDiff = currentMinutes - rolloverStartMinutes;

      // CALCULATOR DISABLED WINDOW: 60 min before, during, 60 min after
      // Total window: 21:00 UTC to 23:05 UTC (2 hours 5 minutes)
      // This protects accounts from spread widening and prevents bad data
      if (minutesDiff >= -60 && minutesDiff <= 65) {
        setCalculatorDisabled(true);
        
        if (minutesDiff < 0) {
          // Before rollover (21:00 - 21:59) - 60 minutes
          setSessionWarning({
            status: "rollover_approaching",
            minutesUntil: Math.abs(minutesDiff),
            totalMinutesUntilResume: Math.abs(minutesDiff) + 65 // Until 60 min after rollover ends
          });
        } else if (minutesDiff >= 0 && minutesDiff <= 5) {
          // During rollover (22:00 - 22:05) - 5 minutes
          setSessionWarning({
            status: "rollover_active",
            minutesRemaining: 5 - minutesDiff,
            totalMinutesUntilResume: 65 - minutesDiff // Remaining closure + 60 min after
          });
        } else {
          // After rollover (22:06 - 23:05) - 60 minutes
          setSessionWarning({
            status: "rollover_recovery",
            minutesSince: minutesDiff - 5,
            minutesUntilResume: 65 - minutesDiff // Time remaining in 60-min recovery period
          });
        }
        return;
      }

      // Outside rollover window - enable calculator
      setCalculatorDisabled(false);
      setSessionWarning(null);
    };

    // Check immediately
    checkRolloverWarning();

    // Check every minute
    const interval = setInterval(checkRolloverWarning, 60000);

    return () => clearInterval(interval);
  }, []);


  // Fetch active breakout alerts
  useEffect(() => {
    const fetchBreakoutAlerts = async () => {
      try {
        const response = await axios.get(`${API}/breakout-alerts`);
        if (response.data.success) {
          setBreakoutAlerts(response.data.alerts || []);
        }
      } catch (error) {
        console.error("Error fetching breakout alerts:", error);
      }
    };

    // Fetch immediately on mount
    fetchBreakoutAlerts();

    // Poll every 30 seconds for new alerts
    const interval = setInterval(fetchBreakoutAlerts, 30000);

    return () => clearInterval(interval);
  }, []);

  // 🎯 NEW: Fetch active trades to track which pairs have live trades (for pulsating buttons)
  useEffect(() => {
    const fetchActiveTrades = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        if (!token) return;
        
        const response = await axios.get(`${API}/tracked-trades/analytics`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.data && response.data.success) {
          const recentTrades = response.data.recent_trades || [];
          const activeTradesMap = {};
          
          // Build map of pairs with active trades
          recentTrades.forEach(trade => {
            if (trade.status === 'ACTIVE') {
              activeTradesMap[trade.pair] = {
                trade_id: trade.trade_id,
                direction: trade.direction,
                entry_price: trade.entry_price
              };
            }
          });
          
          setActiveTradePairs(activeTradesMap);
        }
      } catch (error) {
        console.error("Error fetching active trades:", error);
      }
    };

    // Fetch immediately on mount
    fetchActiveTrades();

    // Poll every 10 seconds to keep button states in sync
    const interval = setInterval(fetchActiveTrades, 10000);

    return () => clearInterval(interval);
  }, []);

  // 🎯 NEW: Update current time every second for opportunity timers
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    
    return () => clearInterval(timer);
  }, []);

  // 🎯 NEW: Helper function to format elapsed time since opportunity was detected
  const formatElapsedTime = (detectedAt) => {
    if (!detectedAt) return '';
    
    const detected = new Date(detectedAt);
    const now = currentTime;
    const diffMs = now - detected;
    
    if (diffMs < 0) return '0m';
    
    const diffSeconds = Math.floor(diffMs / 1000);
    const hours = Math.floor(diffSeconds / 3600);
    const minutes = Math.floor((diffSeconds % 3600) / 60);
    const seconds = diffSeconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  };

  // 🎯 NEW: Get color for timer based on age (green = fresh, yellow = moderate, red = old)
  const getTimerColor = (detectedAt) => {
    if (!detectedAt) return '#9ca3af';
    
    const detected = new Date(detectedAt);
    const now = currentTime;
    const diffMinutes = (now - detected) / (1000 * 60);
    
    if (diffMinutes < 30) return '#22c55e';      // Green - Fresh opportunity (< 30 min)
    if (diffMinutes < 60) return '#eab308';      // Yellow - Moderate age (30-60 min)
    if (diffMinutes < 120) return '#f97316';     // Orange - Getting old (1-2 hours)
    return '#ef4444';                             // Red - Old opportunity (> 2 hours)
  };

  // 🎯 Calculate Half Stop Loss (for educational purposes - tighter SL option)
  const calculateHalfSL = (entryPrice, stopLoss, pair) => {
    if (!entryPrice || !stopLoss) return null;
    
    const entry = parseFloat(entryPrice);
    const sl = parseFloat(stopLoss);
    
    if (isNaN(entry) || isNaN(sl)) return null;
    
    // Half SL = Entry + ((SL - Entry) / 2)
    const halfSL = entry + ((sl - entry) / 2);
    
    // Determine decimal places based on pair (JPY pairs use 3, others use 5)
    const isJPYPair = pair && pair.includes('JPY');
    const decimals = isJPYPair ? 3 : 5;
    
    return halfSL.toFixed(decimals);
  };

  // 🎯 NEW: Fetch Trade Opportunities Scanner
  useEffect(() => {
    const fetchTradeOpportunities = async () => {
      try {
        setOpportunitiesLoading(true);
        const token = localStorage.getItem('auth_token'); // 🔧 FIX: Use correct key
        const response = await axios.get(`${API}/trade-opportunities`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.data.success) {
          const newOpportunities = response.data.opportunities || [];
          
          // 🔄 ROLLOVER: Show message if in rollover period
          if (response.data.is_rollover) {
            toast.info("🔄 Daily rollover in progress. Opportunities will refresh after 23:05 UTC.", {
              duration: 10000
            });
            setTradeOpportunities([]);
            setLastOpportunityCount(0);
            return;
          }
          
          // Check if we have NEW opportunities (play bell chime)
          // 🎯 FILTER: Only show STRENGTHENING momentum opportunities (Feb 13, 2026)
          // Users should only trade notifications - STABLE/BUILDING can flip direction
          const strengthenedOpportunities = newOpportunities.filter(
            opp => opp.momentum_direction?.direction === 'STRENGTHENING'
          );
          
          if (strengthenedOpportunities.length > lastOpportunityCount && lastOpportunityCount > 0) {
            // New opportunity detected - play bell chime
            try {
              // Create and play bell chime sound
              const audioContext = new (window.AudioContext || window.webkitAudioContext)();
              const oscillator = audioContext.createOscillator();
              const gainNode = audioContext.createGain();
              
              oscillator.connect(gainNode);
              gainNode.connect(audioContext.destination);
              
              // Bell-like sound: high frequency with quick decay
              oscillator.frequency.setValueAtTime(830, audioContext.currentTime); // E5 note
              oscillator.type = 'sine';
              
              gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
              gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
              
              oscillator.start(audioContext.currentTime);
              oscillator.stop(audioContext.currentTime + 0.5);
              
              // Second chime (slightly lower)
              setTimeout(() => {
                const osc2 = audioContext.createOscillator();
                const gain2 = audioContext.createGain();
                osc2.connect(gain2);
                gain2.connect(audioContext.destination);
                osc2.frequency.setValueAtTime(659, audioContext.currentTime); // E5 lower
                osc2.type = 'sine';
                gain2.gain.setValueAtTime(0.25, audioContext.currentTime);
                gain2.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.6);
                osc2.start(audioContext.currentTime);
                osc2.stop(audioContext.currentTime + 0.6);
              }, 150);
              
              toast.success(`🔔 ${strengthenedOpportunities.length - lastOpportunityCount} new trade opportunity detected!`, {
                duration: 5000
              });
            } catch (audioErr) {
              console.log("Audio notification not available:", audioErr);
            }
          }
          
          // 🎯 Only store STRENGTHENING opportunities (Feb 13, 2026)
          setTradeOpportunities(strengthenedOpportunities);
          setLastOpportunityCount(strengthenedOpportunities.length);
          
          // 🌍 Capture macro environment data
          if (response.data.macro_environment) {
            setMacroEnvironment(response.data.macro_environment);
          }
          
          // 🎯 CRITICAL: Clear selected direction if the opportunity no longer exists
          // This prevents users from calculating with a stale direction after market conditions change
          if (selectedDirection && selectedPair) {
            const stillExists = strengthenedOpportunities.some(
              opp => opp.pair === selectedPair && opp.direction === selectedDirection
            );
            if (!stillExists) {
              setSelectedDirection(null);
              toast.warning(`⚠️ ${selectedPair} ${selectedDirection} opportunity expired - market conditions changed`, {
                duration: 4000
              });
            }
          }
        }
      } catch (error) {
        console.error("Error fetching trade opportunities:", error);
      } finally {
        setOpportunitiesLoading(false);
      }
    };

    // Fetch immediately on mount
    fetchTradeOpportunities();

    // Poll every 60 seconds (1 minute) for new opportunities
    const interval = setInterval(fetchTradeOpportunities, 60000);

    return () => clearInterval(interval);
  }, [lastOpportunityCount, selectedDirection, selectedPair]);


  // CRITICAL FIX: Clear stale results when currency pair changes
  // Prevents showing wrong price data (e.g., EURUSD prices when EURAUD selected)
  useEffect(() => {
    setResult(null);
    setVolumeAnalysis(null);
    setError("");
    setTradeId(null);
    setMonitoringActive(false);
  }, [selectedPair]);

  // 🔔 Push Notification: Check subscription status on mount
  useEffect(() => {
    const checkPushStatus = async () => {
      const supported = isPushSupported();
      setPushSupported(supported);
      
      if (supported) {
        const subscribed = await isSubscribed();
        setPushEnabled(subscribed);
      }
    };
    checkPushStatus();
  }, []);

  // 🎯 Trading Mode: Fetch current mode on mount
  useEffect(() => {
    const fetchTradingMode = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        if (!token) return;
        
        const response = await axios.get(`${API}/trading-mode`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.data.success) {
          setTradingMode(response.data.trading_mode);
        }
      } catch (error) {
        console.error("Error fetching trading mode:", error);
      }
    };
    fetchTradingMode();
  }, []);

  // 🎯 Trading Mode: Toggle handler
  const handleToggleTradingMode = async () => {
    if (tradingModeLoading) return;
    
    setTradingModeLoading(true);
    const newMode = tradingMode === "SCALP" ? "SWING" : "SCALP";
    
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.post(`${API}/trading-mode?mode=${newMode}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setTradingMode(newMode);
        setTradeOpportunities([]); // Clear opportunities to refresh with new mode
        setLastOpportunityCount(0);
        
        if (newMode === "SCALP") {
          toast.success("🔥 SCALP MODE activated! Showing only high-momentum setups for quick 10-30 min trades.", {
            duration: 5000
          });
        } else {
          toast.success("📊 SWING MODE activated! Showing earlier opportunities for longer trades.", {
            duration: 5000
          });
        }
      } else {
        toast.error("Failed to change trading mode");
      }
    } catch (error) {
      console.error("Trading mode error:", error);
      toast.error("Failed to change trading mode: " + (error.response?.data?.detail || error.message));
    }
    
    setTradingModeLoading(false);
  };

  // 🔔 Push Notification: Toggle handler
  const handleTogglePush = async () => {
    if (!pushSupported) {
      toast.error("Push notifications are not supported in this browser");
      return;
    }
    
    setPushLoading(true);
    
    try {
      if (!pushEnabled) {
        // Check current permission status first
        const currentPermission = Notification.permission;
        
        if (currentPermission === 'denied') {
          // Permission was previously denied - user needs to enable in browser settings
          toast.error(
            "Notifications are blocked. Please enable them in your browser settings:\n" +
            "1. Click the lock/info icon in your address bar\n" +
            "2. Find 'Notifications' and set to 'Allow'\n" +
            "3. Refresh the page and try again",
            { duration: 8000 }
          );
          setPushLoading(false);
          return;
        }
        
        // Request permission
        const permission = await requestNotificationPermission();
        if (!permission) {
          toast.error(
            "Please allow notifications when prompted by your browser to receive trade alerts",
            { duration: 5000 }
          );
          setPushLoading(false);
          return;
        }
        
        // Subscribe to push
        await subscribeToPush();
        setPushEnabled(true);
        localStorage.setItem('pushNotificationsEnabled', 'true');
        toast.success("🔔 Push notifications enabled! You'll receive alerts for new trade opportunities.");
      } else {
        // Unsubscribe from push notifications. This device is always turned
        // off; if the server didn't confirm, warn (don't show a false success)
        // so the user knows alerts may take a moment to fully stop.
        try {
          await unsubscribeFromPush();
          setPushEnabled(false);
          localStorage.setItem('pushNotificationsEnabled', 'false');
          toast.success("🔕 Push notifications disabled. You won't receive alerts anymore.");
        } catch (unsubError) {
          console.error("Error unsubscribing:", unsubError);
          setPushEnabled(false);
          localStorage.setItem('pushNotificationsEnabled', 'false');
          toast.warning(unsubError?.message || "Alerts turned off on this device, but the server may take a moment to catch up.", { duration: 8000 });
        }
      }
    } catch (error) {
      console.error("Push notification error:", error);
      toast.error(`Failed to update notifications: ${error?.message || 'please try again'}`, { duration: 8000 });
    }
    
    setPushLoading(false);
  };

  // 🔄 RECALIBRATE: Clear all trade opportunities and start fresh
  const [recalibrateLoading, setRecalibrateLoading] = useState(false);
  
  const handleRecalibrate = async () => {
    if (recalibrateLoading) return;
    
    // Confirm with user
    if (!window.confirm("🔄 RECALIBRATE CALCULATOR?\n\nThis will:\n• Clear ALL trade opportunities\n• Reset momentum history\n• Start fresh with next scan\n\nContinue?")) {
      return;
    }
    
    setRecalibrateLoading(true);
    
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.post(`${API}/recalibrate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        // Clear local state
        setTradeOpportunities([]);
        setLastOpportunityCount(0);
        
        const cooldownMinutes = response.data.cooldown?.minutes || 10;
        toast.success(`🔄 Recalibrated! All pairs on ${cooldownMinutes}-minute cooldown. Fresh opportunities will appear after cooldown expires.`, {
          duration: 8000
        });
      } else {
        toast.error("Failed to recalibrate calculator");
      }
    } catch (error) {
      console.error("Recalibrate error:", error);
      toast.error("Failed to recalibrate: " + (error.response?.data?.detail || error.message));
    }
    
    setRecalibrateLoading(false);
  };

  // Handle pair selection from breakout alerts
  const handleSelectPairFromAlert = (pair) => {
    setSelectedPair(pair);
    // Scroll to top to show the selected pair
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // 🎯 NEW: Close an active trade (when clicking pulsating button)
  const handleCloseTrade = async (pair) => {
    const activeTrade = activeTradePairs[pair];
    if (!activeTrade) {
      toast.error("No active trade found for this pair");
      return;
    }
    
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      
      toast.loading(`Closing ${pair} ${activeTrade.direction} trade...`);
      
      const response = await axios.post(`${API}/close-trade/${activeTrade.trade_id}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.dismiss();
      
      if (response.data.success) {
        toast.success(`✅ Trade closed: ${pair} ${activeTrade.direction} | P&L: ${response.data.final_pnl || 'Calculated'}`, {
          duration: 5000
        });
        
        // Remove from active trades map immediately
        setActiveTradePairs(prev => {
          const updated = { ...prev };
          delete updated[pair];
          return updated;
        });
        
        // Clear selection
        setSelectedDirection(null);
        setResult(null);
      } else {
        toast.error(`Failed to close trade: ${response.data.detail || response.data.message || 'Unknown error'}`);
      }
    } catch (error) {
      toast.dismiss();
      console.error("Error closing trade:", error);
      toast.error(`Error closing trade: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // 🎯 NEW: Handle pair button click - either close trade OR select pair
  const handlePairButtonClick = (pair) => {
    // Check if this pair has an active trade (pulsating button)
    if (activeTradePairs[pair]) {
      // Pair has active trade - ask to close it
      handleCloseTrade(pair);
      return;
    }
    
    // No active trade - proceed with normal selection
    // Check if this pair is a highlighted opportunity
    const opportunity = tradeOpportunities.find(opp => opp.pair === pair);
    if (opportunity) {
      // It's an opportunity - set direction and select pair
      setSelectedDirection(opportunity.direction);
      setSelectedPair(pair);
      toast.info(`${pair} ${opportunity.direction} opportunity selected - Click "Activate ${opportunity.direction} Trade" to execute`, {
        duration: 3000
      });
    } else {
      // Normal pair selection
      setSelectedDirection(null);
      setSelectedPair(pair);
    }
  };

  // 🎯 NEW: Activate trade directly from opportunity (BYPASSES calculator filters)
  const handleActivateFromOpportunity = async () => {
    if (!selectedPair || !riskAmount || !selectedDirection) {
      toast.error("Please enter risk amount to activate the trade");
      return;
    }

    if (parseFloat(riskAmount) <= 0) {
      toast.error("Risk amount must be greater than 0");
      return;
    }

    setLoading(true);
    setError("");

    try {
      // 🔧 FIX: Call the new activate-from-opportunity endpoint that BYPASSES filters
      // This endpoint doesn't re-run the calculator filters because the opportunity
      // was already validated by the scanner
      const response = await axios.post(`${API}/activate-from-opportunity`, {
        pair: selectedPair,
        direction: selectedDirection,
        risk_amount: parseFloat(riskAmount),
        account_balance: accountBalance ? parseFloat(accountBalance) : 10000,
        timeframe: timeframe
      });

      // Clear selected direction after successful trade
      setSelectedDirection(null);
      
      if (response.data.success) {
        toast.success(`✅ Trade Activated: ${selectedPair} ${selectedDirection} @ ${response.data.entry_price}`, {
          duration: 5000
        });
        // Set result to show the trade card
        setResult(response.data);
        setShowChart(false);
        
        // Store trade ID for monitoring
        const calculatedTradeId = response.data.trade_id || null;
        setTradeId(calculatedTradeId);
        setMonitoringActive(true);
        
        // 🎯 NEW: Add to active trades map for pulsating button
        setActiveTradePairs(prev => ({
          ...prev,
          [selectedPair]: {
            trade_id: calculatedTradeId,
            direction: selectedDirection,
            entry_price: response.data.entry_price
          }
        }));
      } else {
        toast.error(`Failed to activate: ${response.data.detail || response.data.message || 'Unknown error'}`, {
          duration: 5000
        });
      }
    } catch (err) {
      console.error("Activate trade error:", err);
      const errorMessage = err.response?.data?.detail || err.message || "Failed to activate trade";
      toast.error(errorMessage);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCalculate = async () => {
    // CRITICAL: Block calculation during rollover period
    if (calculatorDisabled) {
      toast.error("🛑 Calculator is disabled during the daily rollover period. Please wait until the protection window ends.");
      return;
    }

    if (!selectedPair || !riskAmount) {
      toast.error("Please select a currency pair and enter risk amount");
      return;
    }

    if (parseFloat(riskAmount) <= 0) {
      toast.error("Risk amount must be greater than 0");
      return;
    }

    if (accountBalance && parseFloat(accountBalance) > 0) {
      const riskPercent = (parseFloat(riskAmount) / parseFloat(accountBalance)) * 100;
      if (riskPercent > 5) {
        toast.warning(`Warning: You're risking ${riskPercent.toFixed(1)}% of your account! Recommended: 1-2%`);
      }
    }

    setLoading(true);
    setError("");
    setResult(null);
    setVolumeAnalysis(null);

    try {
      const response = await axios.post(`${API}/calculate-trade`, {
        pair: selectedPair,
        risk_amount: parseFloat(riskAmount),
        account_balance: accountBalance ? parseFloat(accountBalance) : null,
        enable_auto_sl: enableAutoSL,
        profit_alert_percent: profitAlertPercent,
        trailing_stops: enableAutoSL ? trailingStops : [],
        enable_partial_tp: enablePartialTP,
        partial_tp_levels: enablePartialTP ? partialTPLevels : [],
        timeframe: timeframe,
        // 🎯 NEW: Include direction from Trade Opportunity Scanner
        direction: selectedDirection  // Will be null for manual calculations, or "BUY"/"SELL" for opportunities
      });

      // Clear selected direction after successful calculation
      setSelectedDirection(null);
      
      // Store calculated result and show interactive chart first
      setCalculatedResult(response.data);
      setShowChart(true);
      toast.success("Trade calculated! Adjust your levels on the chart.");
      
      // Store trade ID for monitoring
      const calculatedTradeId = response.data.trade_id || null;
      setTradeId(calculatedTradeId);
      setMonitoringActive(false);
    } catch (err) {
      console.error("Calculate trade error:", err);
      console.error("Error response:", err.response);
      
      // Check if error has structured response with error_code
      let errorDetail = err.response?.data?.detail;
      
      // Try to parse if it's a JSON string
      if (errorDetail && typeof errorDetail === 'string') {
        try {
          errorDetail = JSON.parse(errorDetail);
        } catch (e) {
          // Not JSON, keep as string
        }
      }
      
      if (errorDetail && typeof errorDetail === 'object' && errorDetail.error_code) {
        // Structured error with code
        const { message, error_code } = errorDetail;
        
        if (error_code === 'TRIAL_LIMIT_REACHED' || error_code === 'SUBSCRIPTION_EXPIRED') {
          // Show subscription modal
          setSubscriptionModalMessage(message);
          setShowSubscriptionModal(true);
        } else {
          toast.error(message || "Error calculating trade");
        }
      } else {
        // Simple string error
        const errorMessage = typeof errorDetail === 'string' ? errorDetail : err.message || "Error calculating trade";
        setError(errorMessage);
        toast.error(`Failed to calculate trade: ${errorMessage}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchVolumeAnalysis = async (pair) => {
    setLoadingVolume(true);
    try {
      const response = await axios.get(`${API}/volume-analysis/${pair}`);
      setVolumeAnalysis(response.data);
    } catch (err) {
      console.error("Error fetching volume analysis:", err);
      toast.error("Could not fetch volume data");
    } finally {
      setLoadingVolume(false);
    }
  };
  
  // Handle chart confirmation - user confirmed adjusted levels
  const handleChartConfirm = (adjustedValues) => {
    // Update the result with adjusted values
    const updatedResult = {
      ...calculatedResult,
      entry_price: adjustedValues.entry,
      stop_loss: adjustedValues.stopLoss,
      take_profit: adjustedValues.takeProfit,
      lot_size: adjustedValues.lotSize
    };
    
    setResult(updatedResult);
    setShowChart(false);
    setCalculatedResult(null);
    toast.success("Trade parameters confirmed!");
    
    // Fetch volume analysis after confirmation
    fetchVolumeAnalysis(selectedPair);
  };
  
  // Handle chart cancellation - go back to calculator
  const handleChartCancel = () => {
    setShowChart(false);
    setCalculatedResult(null);
    toast.info("Trade calculation cancelled");
  };

  const handleActivateMonitoring = async () => {
    if (!tradeId) {
      toast.error("No trade ID available");
      return;
    }

    try {
      const response = await axios.post(`${API}/activate-monitoring/${tradeId}`);
      
      if (response.data.success) {
        setMonitoringActive(true);
        toast.success("🤖 Automated monitoring activated! Stop loss will adjust automatically.");
      } else {
        toast.error(response.data.message);
      }
    } catch (err) {
      toast.error("Failed to activate monitoring");
    }
  };

  const handleDeactivateMonitoring = async () => {
    if (!tradeId) return;

    try {
      const response = await axios.post(`${API}/deactivate-monitoring/${tradeId}`);
      
      if (response.data.success) {
        setMonitoringActive(false);
        toast.info("Monitoring deactivated");
      }
    } catch (err) {
      toast.error("Failed to deactivate monitoring");
    }
  };

  const addTrailingStop = () => {
    setTrailingStops([...trailingStops, { profit_target_percent: 75, move_sl_to_percent: 25 }]);
  };

  const removeTrailingStop = (index) => {
    setTrailingStops(trailingStops.filter((_, i) => i !== index));
  };

  const updateTrailingStop = (index, field, value) => {
    const updated = [...trailingStops];
    updated[index][field] = parseFloat(value);
    setTrailingStops(updated);
  };

  const handlePlaceTrade = async () => {
    if (!result) return;

    try {
      const response = await axios.post(`${API}/mt5/place-trade`, {
        pair: result.pair,
        direction: result.direction,
        lot_size: result.lot_size,
        stop_loss: result.stop_loss,
        take_profit: result.take_profit
      });

      if (response.data.success) {
        toast.success("Trade placed successfully on MT5!");
      } else {
        toast.error(response.data.message);
      }
    } catch (err) {
      console.error("MT5 place trade error:", err);
      
      // Check if error has structured response with error_code
      let errorDetail = err.response?.data?.detail;
      
      // Try to parse if it's a JSON string
      if (errorDetail && typeof errorDetail === 'string') {
        try {
          errorDetail = JSON.parse(errorDetail);
        } catch (e) {
          // Not JSON, keep as string
        }
      }
      
      if (errorDetail && typeof errorDetail === 'object' && errorDetail.error_code) {
        const { message, error_code } = errorDetail;
        
        if (error_code === 'MT5_REQUIRES_SUBSCRIPTION' || error_code === 'SUBSCRIPTION_EXPIRED') {
          // Show subscription modal
          setSubscriptionModalMessage(message);
          setShowSubscriptionModal(true);
        } else {
          toast.error(message || "Failed to place trade on MT5");
        }
      } else {
        toast.error("Failed to place trade on MT5");
      }
    }
  };

  const handleSetTrade = () => {
    if (!result) return;

    // Check if user is on trial - block MT5 feature
    if (subscriptionInfo && subscriptionInfo.current_tier === "trial") {
      setShowSubscriptionModal(true);
      return;
    }

    // Create trade details for copying
    const tradeDetails = `
TRADE BLINDFOLDED - TRADE SETUP

Pair: ${result.pair}
Direction: ${result.direction}
Lot Size: ${result.lot_size}

Entry Price: ${result.entry_price}
Stop Loss: ${result.stop_loss}
Take Profit: ${result.take_profit}

Risk Amount: $${result.risk_amount}
Potential Profit: $${result.potential_profit}

Setup Instructions:
1. Open MetaTrader 5 app
2. Search for ${result.pair}
3. Tap "Trade" to open new order
4. Select order type (Market, Limit, Stop, etc.)
5. Set Volume: ${result.lot_size}
6. Set Stop Loss: ${result.stop_loss}
7. Set Take Profit: ${result.take_profit}
8. Choose ${result.direction} direction
9. Confirm and place trade
    `.trim();

    // Copy to clipboard
    navigator.clipboard.writeText(tradeDetails).then(() => {
      toast.success("Trade details copied to clipboard!");
    }).catch(() => {
      toast.error("Failed to copy. Please copy manually.");
    });

    // Try to open MT5 app (will work on mobile if MT5 is installed)
    // MT5 doesn't support deep linking with parameters, so we just open the app
    setTimeout(() => {
      window.location.href = "mt5://";
    }, 500);
  };

  return (
    <div className="forex-calculator-container">
      {/* Yellow "Breakout Alerts Detected" banner REMOVED per user request (Apr 24, 2026).
          Pocket Audit panel (below the logout row) now carries the breakout-related UX. */}
      
      <div className="forex-header">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", maxWidth: "900px", margin: "0 auto" }}>
          <div style={{ flex: 1 }}>
            <h1 className="forex-title" data-testid="app-title">Trade Blindfolded</h1>
            <p className="forex-subtitle">Calculate highest probability trades based on historical data</p>
          </div>
          {onLogout && (
            <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
              {/* Show Admin Dashboard for admin, Profile for regular users */}
              {localStorage.getItem('user_email') === 'kalebarancelovic@outlook.com' ? (
                <Link
                  to="/admin-dashboard"
                  style={{
                    padding: "0.5rem 1rem",
                    background: "linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)",
                    border: "2px solid #d97706",
                    borderRadius: "8px",
                    color: "white",
                    fontSize: "0.875rem",
                    fontWeight: "600",
                    cursor: "pointer",
                    textDecoration: "none",
                    textAlign: "center",
                    transition: "all 0.3s ease",
                    display: "block",
                    boxShadow: "0 2px 8px rgba(245, 158, 11, 0.3)"
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = "translateY(-1px)";
                    e.target.style.boxShadow = "0 4px 12px rgba(245, 158, 11, 0.5)";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = "translateY(0)";
                    e.target.style.boxShadow = "0 2px 8px rgba(245, 158, 11, 0.3)";
                  }}
                  data-testid="admin-button"
                >
                  ⚙️ Dashboard
                </Link>
              ) : (
                <Link
                  to="/profile"
                  style={{
                    padding: "1rem 2rem",
                    background: "linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%)",
                    border: "3px solid #7c3aed",
                    borderRadius: "12px",
                    color: "white",
                    fontSize: "1rem",
                    fontWeight: "700",
                    cursor: "pointer",
                    textDecoration: "none",
                    textAlign: "center",
                    transition: "all 0.3s ease",
                    display: "block",
                    boxShadow: "0 4px 12px rgba(139, 92, 246, 0.4)"
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = "translateY(-2px)";
                    e.target.style.boxShadow = "0 6px 20px rgba(139, 92, 246, 0.6)";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = "translateY(0)";
                    e.target.style.boxShadow = "0 4px 12px rgba(139, 92, 246, 0.4)";
                  }}
                  data-testid="profile-button"
                >
                  👤 My Profile
                </Link>
              )}
              {/* 🔔 Push Notification Toggle */}
              {pushSupported && (
                <button
                  onClick={handleTogglePush}
                  disabled={pushLoading}
                  style={{
                    padding: "0.5rem 1rem",
                    background: pushEnabled 
                      ? "linear-gradient(135deg, #10b981 0%, #34d399 100%)"
                      : "rgba(107, 114, 128, 0.2)",
                    border: `2px solid ${pushEnabled ? "#059669" : "#6b7280"}`,
                    borderRadius: "8px",
                    color: pushEnabled ? "white" : "#9ca3af",
                    fontSize: "0.8rem",
                    fontWeight: "600",
                    cursor: pushLoading ? "wait" : "pointer",
                    transition: "all 0.3s ease",
                    opacity: pushLoading ? 0.7 : 1
                  }}
                  onMouseEnter={(e) => {
                    if (!pushLoading) {
                      e.target.style.transform = "translateY(-1px)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = "translateY(0)";
                  }}
                  data-testid="push-notification-toggle"
                  title={pushEnabled ? "Push notifications enabled" : "Enable push notifications"}
                >
                  {pushLoading ? "..." : pushEnabled ? "🔔 Alerts On" : "🔕 Alerts Off"}
                </button>
              )}
              <button
                onClick={onLogout}
                style={{
                  padding: "0.5rem 1rem",
                  background: "rgba(239, 68, 68, 0.1)",
                  border: "2px solid #ef4444",
                  borderRadius: "8px",
                  color: "#b91c1c",
                  fontSize: "0.8rem",
                  fontWeight: "600",
                  cursor: "pointer",
                  transition: "all 0.3s ease"
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = "rgba(239, 68, 68, 0.2)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = "rgba(239, 68, 68, 0.1)";
                }}
                data-testid="logout-button"
              >
                Logout
              </button>
            </div>
          )}
        </div>
      </div>

      {/* 🎯 Daily Pocket Audit RETIRED & REMOVED (Jun 2026, operator request) — panel fully
          removed from the UI. Superseded by the 🔬 Notification Lab Goldilocks work.
          Backend pocket-audit endpoint/scheduler left intact but no longer surfaced. */}

      {/* Daily Rollover Warning Card - CALCULATOR DISABLED */}
      {sessionWarning && calculatorDisabled && (
        <div style={{
          maxWidth: "900px",
          margin: "2rem auto 1rem",
          padding: "2rem",
          background: "linear-gradient(135deg, #7f1d1d 0%, #991b1b 100%)",
          border: "4px solid #dc2626",
          borderRadius: "16px",
          boxShadow: "0 12px 32px rgba(220, 38, 38, 0.5)",
          animation: "pulse 2s ease-in-out infinite"
        }}>
          <div style={{
            display: "flex",
            alignItems: "flex-start",
            gap: "1.5rem"
          }}>
            {/* Warning Icon */}
            <div style={{
              fontSize: "4rem",
              flexShrink: 0
            }}>
              {sessionWarning.status === "rollover_active" ? "🚨" : "🛑"}
            </div>

            {/* Warning Content */}
            <div style={{ flex: 1 }}>
              <h3 style={{
                fontSize: "1.5rem",
                fontWeight: "700",
                color: "#fef2f2",
                marginBottom: "1rem",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem"
              }}>
                {sessionWarning.status === "rollover_active" ? "🔴 MARKET CLOSED - CALCULATOR DISABLED" : "⛔ CALCULATOR DISABLED - ROLLOVER PROTECTION"}
              </h3>
              
              {sessionWarning.status === "rollover_approaching" ? (
                <div>
                  <p style={{
                    fontSize: "1.25rem",
                    color: "#fef2f2",
                    marginBottom: "0.75rem",
                    lineHeight: "1.6",
                    fontWeight: "700"
                  }}>
                    🔒 CALCULATOR LOCKED - Daily rollover begins in <strong>{sessionWarning.minutesUntil} minutes</strong>
                  </p>
                  <p style={{
                    fontSize: "1.125rem",
                    color: "#fecaca",
                    lineHeight: "1.6",
                    marginBottom: "0.75rem",
                    fontWeight: "600"
                  }}>
                    🕐 Calculator will automatically unlock in <strong>{sessionWarning.totalMinutesUntilResume} minutes</strong>
                  </p>
                  <p style={{
                    fontSize: "0.875rem",
                    color: "#fee2e2",
                    lineHeight: "1.6"
                  }}>
                    All buttons are disabled during the 2-hour protection window to prevent spread widening damage and corrupted data. 
                    The calculator will automatically re-enable when safe conditions return.
                  </p>
                </div>
              ) : sessionWarning.status === "rollover_active" ? (
                <div>
                  <p style={{
                    fontSize: "1.25rem",
                    color: "#fef2f2",
                    marginBottom: "0.75rem",
                    lineHeight: "1.6",
                    fontWeight: "700"
                  }}>
                    🔒 CALCULATOR LOCKED - <strong>MARKET CLOSED</strong> (Settlement Active)
                  </p>
                  <p style={{
                    fontSize: "1.125rem",
                    color: "#fecaca",
                    lineHeight: "1.6",
                    marginBottom: "0.75rem",
                    fontWeight: "600"
                  }}>
                    🕐 Calculator will automatically unlock in <strong>{sessionWarning.totalMinutesUntilResume} minutes</strong>
                  </p>
                  <p style={{
                    fontSize: "0.875rem",
                    color: "#fee2e2",
                    lineHeight: "1.6"
                  }}>
                    Market is closed for swap/rollover calculation. All calculator functions are disabled and will automatically 
                    re-enable 1 hour after market reopens to allow spreads to normalize.
                  </p>
                </div>
              ) : (
                <div>
                  <p style={{
                    fontSize: "1.25rem",
                    color: "#fef2f2",
                    marginBottom: "0.75rem",
                    lineHeight: "1.6",
                    fontWeight: "700"
                  }}>
                    🔒 CALCULATOR LOCKED - Market reopened {sessionWarning.minutesSince} minutes ago
                  </p>
                  <p style={{
                    fontSize: "1.125rem",
                    color: "#fecaca",
                    lineHeight: "1.6",
                    marginBottom: "0.75rem",
                    fontWeight: "600"
                  }}>
                    🕐 Calculator will automatically unlock in <strong>{sessionWarning.minutesUntilResume} minutes</strong>
                  </p>
                  <p style={{
                    fontSize: "0.875rem",
                    color: "#fee2e2",
                    lineHeight: "1.6"
                  }}>
                    Waiting for spreads to normalize. All calculator functions are disabled during the 1-hour recovery period 
                    and will automatically re-enable when safe trading conditions return.
                  </p>
                </div>
              )}

              {/* Additional Info Box */}
              <div style={{
                marginTop: "1.25rem",
                padding: "1rem",
                background: "rgba(254, 226, 226, 0.2)",
                borderRadius: "8px",
                border: "2px solid rgba(254, 226, 226, 0.3)",
                fontSize: "0.875rem",
                color: "#fef2f2"
              }}>
                <div style={{ fontWeight: "700", marginBottom: "0.5rem" }}>🛡️ Protection Window (2 hours total):</div>
                <div style={{ lineHeight: "1.8" }}>
                  • <strong>1 hour before</strong> rollover - Spreads start widening<br/>
                  • <strong>5 minutes during</strong> rollover - Market closed<br/>
                  • <strong>1 hour after</strong> rollover - Spreads normalizing
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Interactive Chart - Shows before result cards */}
      {showChart && calculatedResult && (
        <InteractiveTradingChart
          key={`${selectedPair}-${timeframe}`}
          pair={selectedPair}
          initialEntry={calculatedResult.entry_price}
          initialSL={calculatedResult.stop_loss}
          initialTP={calculatedResult.take_profit}
          direction={calculatedResult.direction}
          riskAmount={parseFloat(riskAmount)}
          priceAction={calculatedResult.price_action}
          counterTradeInfo={calculatedResult.price_action?.counter_trade_suggestion}
          onConfirm={handleChartConfirm}
          onCancel={handleChartCancel}
          timeframe={timeframe}
        />
      )}

      {/* Hide calculator form when showing chart */}
      {!showChart && (
      <div className="forex-card">
        <div className="form-section">
          
          {/* High Risk News Warning - Above Calculator */}
          {tradingStatus.block_due_to_news && (
            <div style={{
              padding: '1rem 1.25rem',
              background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.15) 0%, rgba(220, 38, 38, 0.2) 100%)',
              borderRadius: '12px',
              marginBottom: '1.5rem',
              border: '2px solid rgba(239, 68, 68, 0.5)',
              boxShadow: '0 4px 12px rgba(239, 68, 68, 0.2)'
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                <span style={{ fontSize: '1.75rem', flexShrink: 0 }}>🚫</span>
                <div>
                  <strong style={{ 
                    color: '#991b1b', 
                    fontSize: '1rem',
                    display: 'block',
                    marginBottom: '0.5rem'
                  }}>
                    ⚠️ HIGH RISK NEWS - Calculator Restricted
                  </strong>
                  <p style={{ 
                    fontSize: '0.875rem', 
                    margin: 0, 
                    color: '#991b1b',
                    lineHeight: '1.5'
                  }}>
                    {tradingStatus.news_recommendation || "High-impact news event detected"}. 
                    Trading during high-impact news events carries significant risk due to increased volatility and spread widening.
                  </p>
                  {tradingStatus.news_note && (
                    <p style={{ 
                      fontSize: '0.8rem', 
                      margin: '0.5rem 0 0 0', 
                      color: '#b91c1c',
                      fontStyle: 'italic'
                    }}>
                      📰 {tradingStatus.news_note}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
          
          {/* 🌙 Asian Session Warning - London Countdown */}
          {londonCountdown.isAsianSession && (
            <div style={{
              padding: '1rem 1.25rem',
              background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.15) 0%, rgba(245, 158, 11, 0.2) 100%)',
              borderRadius: '12px',
              marginBottom: '1.5rem',
              border: '2px solid rgba(245, 158, 11, 0.5)',
              boxShadow: '0 4px 12px rgba(245, 158, 11, 0.15)'
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                <span style={{ fontSize: '1.75rem', flexShrink: 0 }}>🌙</span>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
                    <strong style={{ 
                      color: '#92400e', 
                      fontSize: '1rem',
                      display: 'block'
                    }}>
                      ⏳ Low Liquidity Session ({londonCountdown.currentSession})
                    </strong>
                    <div style={{
                      background: 'linear-gradient(135deg, #1e3a5f 0%, #0f172a 100%)',
                      padding: '0.5rem 1rem',
                      borderRadius: '8px',
                      border: '2px solid #f59e0b',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem'
                    }}>
                      <span style={{ fontSize: '0.75rem', color: '#fbbf24' }}>🇬🇧 London opens in</span>
                      <span style={{ 
                        fontFamily: 'monospace', 
                        fontSize: '1.1rem', 
                        fontWeight: '700', 
                        color: '#fbbf24',
                        minWidth: '80px',
                        textAlign: 'center'
                      }}>
                        {String(londonCountdown.hoursUntilLondon).padStart(2, '0')}:
                        {String(londonCountdown.minutesUntilLondon).padStart(2, '0')}:
                        {String(londonCountdown.secondsUntilLondon).padStart(2, '0')}
                      </span>
                    </div>
                  </div>
                  <p style={{ 
                    fontSize: '0.85rem', 
                    margin: '0.75rem 0 0 0', 
                    color: '#92400e',
                    lineHeight: '1.5'
                  }}>
                    Market conditions are less ideal during Asian session. Price action may not respect technical levels due to low volume. 
                    <strong> Signals detected now may perform better after London opens at 08:00 UTC.</strong>
                  </p>
                  <div style={{
                    marginTop: '0.75rem',
                    padding: '0.5rem 0.75rem',
                    background: 'rgba(245, 158, 11, 0.1)',
                    borderRadius: '6px',
                    fontSize: '0.8rem',
                    color: '#b45309'
                  }}>
                    💡 <strong>Tip:</strong> Trades detected during this period historically have lower success rates. Consider waiting for London session confirmation.
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* 🎯 Trade Opportunities Alert Panel */}
          {tradeOpportunities.length > 0 && (
            <div style={{
              background: 'linear-gradient(135deg, #1e3a5f 0%, #0f172a 100%)',
              borderRadius: '16px',
              padding: '1.25rem',
              marginBottom: '1.5rem',
              border: '2px solid #22c55e',
              boxShadow: '0 4px 20px rgba(34, 197, 94, 0.2)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ fontSize: '1.5rem' }}>🎯</span>
                  <h3 style={{ margin: 0, color: 'white', fontSize: '1.1rem', fontWeight: '700' }}>
                    Trade Opportunities ({tradeOpportunities.length})
                  </h3>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  {/* 🎯 TRADING MODE TOGGLE */}
                  <button
                    onClick={handleToggleTradingMode}
                    disabled={tradingModeLoading}
                    style={{
                      padding: '0.4rem 0.75rem',
                      background: tradingMode === 'SCALP'
                        ? 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)'
                        : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                      border: 'none',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '0.75rem',
                      fontWeight: '700',
                      cursor: tradingModeLoading ? 'wait' : 'pointer',
                      transition: 'all 0.3s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.35rem',
                      opacity: tradingModeLoading ? 0.7 : 1
                    }}
                    data-testid="trading-mode-toggle"
                    title={tradingMode === 'SCALP' 
                      ? 'SCALP: Quick 10-30 min trades, strict filters' 
                      : 'SWING: Longer trades, relaxed filters'}
                  >
                    {tradingModeLoading ? '...' : (
                      <>
                        {tradingMode === 'SCALP' ? '🔥' : '📊'}
                        {tradingMode}
                      </>
                    )}
                  </button>
                  {opportunitiesLoading && (
                    <span style={{ color: '#94a3b8', fontSize: '0.75rem' }}>Scanning...</span>
                  )}
                </div>
              </div>
              
              {/* ⏱️ GLOBAL CANDLE TIMING INDICATOR */}
              <div 
                data-testid="candle-timing-indicator"
                style={{
                  background: candleTiming.isOptimalWindow 
                    ? 'linear-gradient(135deg, rgba(34, 197, 94, 0.15) 0%, rgba(22, 163, 74, 0.2) 100%)'
                    : candleTiming.isNewCandle
                      ? 'linear-gradient(135deg, rgba(245, 158, 11, 0.15) 0%, rgba(217, 119, 6, 0.2) 100%)'
                      : 'linear-gradient(135deg, rgba(239, 68, 68, 0.15) 0%, rgba(220, 38, 38, 0.2) 100%)',
                  borderRadius: '10px',
                  padding: '0.6rem 1rem',
                  marginBottom: '0.75rem',
                  border: `1px solid ${candleTiming.color}40`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '0.5rem'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ fontSize: '1rem' }}>{candleTiming.emoji}</span>
                  <span style={{ 
                    color: candleTiming.color, 
                    fontWeight: '700', 
                    fontSize: '0.8rem' 
                  }}>
                    {candleTiming.isOptimalWindow 
                      ? '✅ OPTIMAL WINDOW' 
                      : candleTiming.isNewCandle 
                        ? '⏳ NEW CANDLE' 
                        : '⏰ HOUR ENDING'}
                  </span>
                  {candleTiming.isRisky && (
                    <span style={{ color: '#94a3b8', fontSize: '0.7rem' }}>
                      - {candleTiming.warning?.replace(/⚠️|⏰|⏳/g, '').trim()}
                    </span>
                  )}
                </div>
                <div style={{
                  background: candleTiming.color + '30',
                  padding: '0.25rem 0.6rem',
                  borderRadius: '6px',
                  color: candleTiming.color,
                  fontWeight: '700',
                  fontSize: '0.85rem',
                  fontFamily: 'monospace'
                }}>
                  :{String(currentTime.getMinutes()).padStart(2, '0')}
                </div>
              </div>
              
              {/* 🌍 MACRO ENVIRONMENT WARNING BANNER */}
              {macroEnvironment && (macroEnvironment.warnings?.length > 0 || macroEnvironment.overall_risk === 'HIGH') && (
                <div 
                  data-testid="macro-warning-banner"
                  style={{
                    background: macroEnvironment.overall_risk === 'HIGH' 
                      ? 'linear-gradient(135deg, #7f1d1d 0%, #991b1b 100%)'
                      : 'linear-gradient(135deg, #78350f 0%, #92400e 100%)',
                    borderRadius: '10px',
                    padding: '0.75rem 1rem',
                    marginBottom: '0.75rem',
                    border: `1px solid ${macroEnvironment.overall_risk === 'HIGH' ? '#ef4444' : '#f59e0b'}`,
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.75rem'
                  }}
                >
                  <span style={{ fontSize: '1.25rem' }}>
                    {macroEnvironment.overall_risk === 'HIGH' ? '🚨' : '⚠️'}
                  </span>
                  <div style={{ flex: 1 }}>
                    <div style={{ 
                      color: 'white', 
                      fontWeight: '700', 
                      fontSize: '0.85rem',
                      marginBottom: '0.25rem'
                    }}>
                      MACRO ENVIRONMENT: {macroEnvironment.overall_risk} RISK
                    </div>
                    <div style={{ color: '#fecaca', fontSize: '0.75rem', lineHeight: '1.4' }}>
                      {macroEnvironment.warnings?.length > 0 ? (
                        macroEnvironment.warnings.map((warning, idx) => (
                          <div key={idx} style={{ marginBottom: '0.25rem' }}>{warning}</div>
                        ))
                      ) : (
                        <>
                          USD: {macroEnvironment.usd_sentiment} 
                          {macroEnvironment.dxy_change !== 0 && ` (DXY ${macroEnvironment.dxy_change > 0 ? '+' : ''}${macroEnvironment.dxy_change?.toFixed(2)}%)`}
                          {macroEnvironment.blocked_count > 0 && ` | ${macroEnvironment.blocked_count} trades blocked`}
                        </>
                      )}
                    </div>
                    {macroEnvironment.news_alerts > 0 && (
                      <div style={{ 
                        color: '#fcd34d', 
                        fontSize: '0.7rem', 
                        marginTop: '0.25rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.25rem'
                      }}>
                        📰 {macroEnvironment.news_alerts} high-impact news event{macroEnvironment.news_alerts > 1 ? 's' : ''} upcoming
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* 🌍 MACRO STATUS INDICATOR (when low risk / no warnings) */}
              {macroEnvironment && macroEnvironment.overall_risk === 'LOW' && (!macroEnvironment.warnings || macroEnvironment.warnings.length === 0) && (
                <div 
                  data-testid="macro-status-indicator"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.4rem 0.75rem',
                    background: 'rgba(34, 197, 94, 0.15)',
                    borderRadius: '8px',
                    marginBottom: '0.75rem',
                    fontSize: '0.7rem',
                    color: '#86efac'
                  }}
                >
                  <span>🌍</span>
                  <span>Macro: Clear</span>
                  <span style={{ color: '#64748b' }}>|</span>
                  <span>USD: {macroEnvironment.usd_sentiment || 'NEUTRAL'}</span>
                  {macroEnvironment.strongest_currencies?.length > 0 && (
                    <>
                      <span style={{ color: '#64748b' }}>|</span>
                      <span>Strong: {macroEnvironment.strongest_currencies.map(c => c.currency || c).join(', ')}</span>
                    </>
                  )}
                  {macroEnvironment.weakest_currencies?.length > 0 && (
                    <>
                      <span style={{ color: '#64748b' }}>|</span>
                      <span>Weak: {macroEnvironment.weakest_currencies.map(c => c.currency || c).join(', ')}</span>
                    </>
                  )}
                </div>
              )}
              
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', 
                gap: '0.75rem',
                maxHeight: '300px',
                overflowY: 'auto'
              }}>
                {tradeOpportunities.map((opp, idx) => (
                  <div
                    key={`${opp.pair}-${opp.direction}-${idx}`}
                    onClick={() => {
                      setSelectedPair(opp.pair);
                      // 🎯 Auto-set the direction from the opportunity
                      setSelectedDirection(opp.direction);
                      toast.success(`${opp.pair} ${opp.direction} opportunity selected - Enter risk & calculate!`, { duration: 3000 });
                    }}
                    style={{
                      background: opp.direction === 'BUY' 
                        ? 'linear-gradient(135deg, rgba(34, 197, 94, 0.2) 0%, rgba(22, 163, 74, 0.3) 100%)'
                        : 'linear-gradient(135deg, rgba(239, 68, 68, 0.2) 0%, rgba(220, 38, 38, 0.3) 100%)',
                      border: `2px solid ${opp.direction === 'BUY' ? '#22c55e' : '#ef4444'}`,
                      borderRadius: '12px',
                      padding: '0.875rem',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      opacity: candleTiming.isRisky ? 0.85 : 1
                    }}
                    data-testid={`opportunity-${opp.pair}-${opp.direction}`}
                  >
                    {/* ⏱️ CANDLE TIMING WARNING BANNER */}
                    {candleTiming.isRisky && (
                      <div style={{
                        background: candleTiming.isNewCandle 
                          ? 'rgba(245, 158, 11, 0.2)' 
                          : 'rgba(239, 68, 68, 0.2)',
                        border: `1px solid ${candleTiming.color}`,
                        borderRadius: '6px',
                        padding: '0.35rem 0.5rem',
                        marginBottom: '0.5rem',
                        fontSize: '0.65rem',
                        color: candleTiming.color,
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.3rem'
                      }}>
                        {candleTiming.emoji} {candleTiming.warning}
                      </div>
                    )}
                    
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                      <span style={{ 
                        color: 'white', 
                        fontWeight: '800', 
                        fontSize: '1rem',
                        fontFamily: 'Space Grotesk, sans-serif'
                      }}>
                        {opp.pair}
                      </span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                        {/* ⏱️ CANDLE TIMER BADGE - Always visible */}
                        <span style={{
                          background: candleTiming.isOptimalWindow 
                            ? 'rgba(34, 197, 94, 0.3)' 
                            : candleTiming.isNewCandle 
                              ? 'rgba(245, 158, 11, 0.3)' 
                              : 'rgba(239, 68, 68, 0.3)',
                          color: candleTiming.color,
                          padding: '0.15rem 0.4rem',
                          borderRadius: '4px',
                          fontSize: '0.6rem',
                          fontWeight: '600',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.2rem'
                        }}
                        title={candleTiming.isOptimalWindow ? '✅ OPTIMAL WINDOW - Safe to enter' : candleTiming.warning}
                        >
                          ⏱️ :{String(currentTime.getMinutes()).padStart(2, '0')}
                        </span>
                        <span style={{
                          background: opp.direction === 'BUY' ? '#22c55e' : '#ef4444',
                          color: 'white',
                          padding: '0.2rem 0.5rem',
                          borderRadius: '6px',
                          fontSize: '0.7rem',
                          fontWeight: '700'
                        }}>
                          {opp.direction}
                        </span>
                      </div>
                    </div>
                    
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ color: '#94a3b8', fontSize: '0.75rem' }}>
                        <div>🌊 Momentum: +{opp.momentum_score}</div>
                        {opp.momentum_direction && (
                          <div style={{ 
                            color: opp.momentum_direction.direction === 'STRENGTHENING' ? '#22c55e' : 
                                   opp.momentum_direction.direction === 'RECOVERING' ? '#3b82f6' :
                                   opp.momentum_direction.direction === 'STABLE' ? '#94a3b8' : '#f59e0b',
                            fontWeight: '600'
                          }}>
                            {opp.momentum_direction.emoji} {opp.momentum_direction.direction}
                          </div>
                        )}
                        <div>📊 {opp.htf_4h}/{opp.htf_daily}</div>
                      </div>
                      <div style={{
                        background: opp.quality_label === 'PERFECT' ? '#22c55e' : opp.quality_label === 'GOOD' ? '#3b82f6' : '#f59e0b',
                        color: 'white',
                        padding: '0.25rem 0.5rem',
                        borderRadius: '8px',
                        fontSize: '0.7rem',
                        fontWeight: '700',
                        textAlign: 'center'
                      }}>
                        <div>{opp.quality_score}</div>
                        <div style={{ fontSize: '0.6rem', opacity: 0.9 }}>{opp.quality_label}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              <div style={{ 
                marginTop: '0.75rem', 
                paddingTop: '0.75rem', 
                borderTop: '1px solid rgba(255,255,255,0.1)',
                display: 'flex',
                flexDirection: 'column',
                gap: '0.75rem'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <span style={{ color: '#64748b', fontSize: '0.7rem' }}>
                    🔔 Bell chimes when new opportunities detected
                  </span>
                  <span style={{ color: '#64748b', fontSize: '0.7rem' }}>
                    Updates every 60s
                  </span>
                </div>
                
                {/* 🔄 RECALIBRATE BUTTON */}
                <button
                  onClick={handleRecalibrate}
                  disabled={recalibrateLoading}
                  style={{
                    width: '100%',
                    padding: '0.6rem 1rem',
                    background: recalibrateLoading 
                      ? 'rgba(107, 114, 128, 0.3)' 
                      : 'linear-gradient(135deg, rgba(239, 68, 68, 0.2) 0%, rgba(220, 38, 38, 0.2) 100%)',
                    border: '2px solid #ef4444',
                    borderRadius: '8px',
                    color: '#ef4444',
                    fontSize: '0.8rem',
                    fontWeight: '600',
                    cursor: recalibrateLoading ? 'wait' : 'pointer',
                    transition: 'all 0.3s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem'
                  }}
                  onMouseEnter={(e) => {
                    if (!recalibrateLoading) {
                      e.target.style.background = 'linear-gradient(135deg, rgba(239, 68, 68, 0.4) 0%, rgba(220, 38, 38, 0.4) 100%)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!recalibrateLoading) {
                      e.target.style.background = 'linear-gradient(135deg, rgba(239, 68, 68, 0.2) 0%, rgba(220, 38, 38, 0.2) 100%)';
                    }
                  }}
                  data-testid="recalibrate-button"
                >
                  {recalibrateLoading ? (
                    <>⏳ Recalibrating...</>
                  ) : (
                    <>🔄 Recalibrate (Clear All & Start Fresh)</>
                  )}
                </button>
              </div>
            </div>
          )}
          
          {/* Dark "No opportunities in {tradingMode} mode" banner REMOVED per user
              request (Apr 24, 2026). The Pocket Audit panel above now communicates
              protocol state + trading recommendations. */}
          
          {/* Trade opportunities list (shown when opportunities exist) */}
          
          <label className="form-label">Select Currency Pair</label>
          
          {/* Category Tabs */}
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
            <button
              onClick={() => setPairCategory('forex')}
              style={{
                flex: 1,
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                border: pairCategory === 'forex' ? '2px solid #0891b2' : '2px solid #e5e7eb',
                background: pairCategory === 'forex' ? 'linear-gradient(135deg, #0891b2 0%, #06b6d4 100%)' : 'white',
                color: pairCategory === 'forex' ? 'white' : '#334155',
                fontWeight: '700',
                fontSize: '0.875rem',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
            >
              💱 Forex Pairs ({FOREX_PAIRS.length})
            </button>
            <button
              onClick={() => setPairCategory('crypto')}
              style={{
                flex: 1,
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                border: pairCategory === 'crypto' ? '2px solid #0891b2' : '2px solid #e5e7eb',
                background: pairCategory === 'crypto' ? 'linear-gradient(135deg, #0891b2 0%, #06b6d4 100%)' : 'white',
                color: pairCategory === 'crypto' ? 'white' : '#334155',
                fontWeight: '700',
                fontSize: '0.875rem',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
            >
              ₿ Crypto Pairs ({CRYPTO_PAIRS.length})
            </button>
          </div>
          
          {/* Pairs Grid - Filtered by Category */}
          <div className="currency-grid">
            {(pairCategory === 'forex' ? FOREX_PAIRS : CRYPTO_PAIRS).map((pair) => {
              // Check if this pair has an active breakout alert
              const hasBreakoutAlert = breakoutAlerts.some(alert => alert.pair === pair && alert.has_lightning);
              
              // 🎯 Check if this pair has a trade opportunity
              const opportunity = tradeOpportunities.find(opp => opp.pair === pair);
              const hasBuyOpportunity = opportunity && opportunity.direction === 'BUY';
              const hasSellOpportunity = opportunity && opportunity.direction === 'SELL';
              
              // 🎯 NEW: Check if this pair has an active trade (for pulsating button)
              const activeTrade = activeTradePairs[pair];
              const hasActiveTrade = !!activeTrade;
              
              return (
                <div
                  key={pair}
                  className={`currency-option ${selectedPair === pair ? "selected" : ""} ${hasBreakoutAlert ? "electric-shock" : ""} ${hasBuyOpportunity && !hasActiveTrade ? "opportunity-buy-flash" : ""} ${hasSellOpportunity && !hasActiveTrade ? "opportunity-sell-flash" : ""} ${hasActiveTrade ? "active-trade-pulse" : ""}`}
                  onClick={() => {
                    if (calculatorDisabled) return;
                    // 🎯 NEW: Use the unified click handler
                    handlePairButtonClick(pair);
                  }}
                  data-testid={`currency-pair-${pair}`}
                  title={hasActiveTrade 
                    ? `🔴 ACTIVE ${activeTrade.direction} TRADE - Click to close` 
                    : opportunity 
                      ? `${opportunity.direction} Opportunity - Quality: ${opportunity.quality_label} (${opportunity.quality_score})` 
                      : ''
                  }
                  style={{
                    opacity: calculatorDisabled ? 0.5 : 1,
                    cursor: calculatorDisabled ? 'not-allowed' : 'pointer',
                    pointerEvents: calculatorDisabled ? 'none' : 'auto',
                    position: 'relative',
                    overflow: 'visible',  // 🔧 FIX: Allow timer to show outside button
                    marginBottom: opportunity && !hasActiveTrade ? '16px' : '0',  // 🔧 FIX: Space for timer
                    // 🎯 NEW: Active trade styling
                    border: hasActiveTrade 
                      ? `3px solid ${activeTrade.direction === 'BUY' ? '#22c55e' : '#ef4444'}` 
                      : undefined,
                    boxShadow: hasActiveTrade 
                      ? `0 0 15px ${activeTrade.direction === 'BUY' ? 'rgba(34, 197, 94, 0.6)' : 'rgba(239, 68, 68, 0.6)'}` 
                      : undefined
                  }}
                >
                  <div className="currency-name">{pair}</div>
                  {/* 🎯 Active trade indicator */}
                  {hasActiveTrade && (
                    <div style={{
                      position: 'absolute',
                      top: '-12px',
                      right: '-12px',
                      background: activeTrade.direction === 'BUY' ? '#22c55e' : '#ef4444',
                      color: 'white',
                      fontSize: '0.7rem',
                      fontWeight: '800',
                      padding: '4px 8px',
                      borderRadius: '12px',
                      boxShadow: `0 2px 8px ${activeTrade.direction === 'BUY' ? 'rgba(34, 197, 94, 0.6)' : 'rgba(239, 68, 68, 0.6)'}`,
                      animation: 'live-flash 0.8s ease-in-out infinite',
                      zIndex: 20,
                      border: '2px solid white'
                    }}>
                      {activeTrade.direction === 'BUY' ? '📈' : '📉'} LIVE
                    </div>
                  )}
                  {/* Quality badge for opportunities (only show if no active trade) */}
                  {opportunity && !hasActiveTrade && (
                    <>
                      <div style={{
                        position: 'absolute',
                        top: '-8px',
                        right: '-8px',
                        background: hasBuyOpportunity ? '#22c55e' : '#ef4444',
                        color: 'white',
                        fontSize: '0.65rem',
                        fontWeight: '700',
                        padding: '2px 6px',
                        borderRadius: '10px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                      }}>
                        {opportunity.quality_score}
                      </div>
                      {/* 🎯 Timer - Shows elapsed time since opportunity detected */}
                      {opportunity.detected_at && (
                        <div 
                          style={{
                            position: 'absolute',
                            bottom: '-12px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
                            color: getTimerColor(opportunity.detected_at),
                            fontSize: '0.7rem',
                            fontWeight: '800',
                            padding: '4px 10px',
                            borderRadius: '12px',
                            boxShadow: '0 3px 8px rgba(0,0,0,0.4), 0 0 0 2px rgba(255,255,255,0.1)',
                            whiteSpace: 'nowrap',
                            fontFamily: 'monospace',
                            zIndex: 10,
                            border: `1px solid ${getTimerColor(opportunity.detected_at)}`,
                            letterSpacing: '0.5px'
                          }}
                          data-testid={`timer-${pair}`}
                        >
                          {formatElapsedTime(opportunity.detected_at)}
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        
        {/* 🎯 Direction Indicator (shows when opportunity direction is selected) */}
        {selectedDirection && (
          <div style={{
            background: selectedDirection === 'BUY' 
              ? 'linear-gradient(135deg, rgba(34, 197, 94, 0.15) 0%, rgba(22, 163, 74, 0.2) 100%)'
              : 'linear-gradient(135deg, rgba(239, 68, 68, 0.15) 0%, rgba(220, 38, 38, 0.2) 100%)',
            border: `2px solid ${selectedDirection === 'BUY' ? '#22c55e' : '#ef4444'}`,
            borderRadius: '12px',
            padding: '1rem',
            marginBottom: '1rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <span style={{ fontSize: '1.5rem' }}>{selectedDirection === 'BUY' ? '📈' : '📉'}</span>
              <div>
                <div style={{ 
                  color: selectedDirection === 'BUY' ? '#16a34a' : '#dc2626', 
                  fontWeight: '800', 
                  fontSize: '1.1rem' 
                }}>
                  {selectedPair} {selectedDirection}
                </div>
                <div style={{ color: '#64748b', fontSize: '0.8rem' }}>
                  Direction locked from opportunity scanner
                </div>
              </div>
            </div>
            <button
              onClick={() => {
                setSelectedDirection(null);
                toast.info('Direction cleared - calculator will auto-detect direction');
              }}
              style={{
                background: 'rgba(255,255,255,0.1)',
                border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: '8px',
                padding: '0.5rem 1rem',
                color: '#94a3b8',
                fontSize: '0.75rem',
                cursor: 'pointer'
              }}
            >
              ✕ Clear
            </button>
          </div>
        )}

        <div className="form-section">
          <label className="form-label">Select Timeframe</label>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {['1h', '4h', '1D'].map((tf) => (
              <button
                key={tf}
                onClick={() => {
                  console.log(`🕐 Timeframe button clicked: ${tf}`);
                  setTimeframe(tf);
                }}
                disabled={calculatorDisabled}
                style={{
                  padding: '0.75rem 1.5rem',
                  borderRadius: '8px',
                  border: timeframe === tf ? '2px solid #0891b2' : '2px solid #e5e7eb',
                  background: timeframe === tf ? 'linear-gradient(135deg, #0891b2 0%, #06b6d4 100%)' : 'white',
                  color: timeframe === tf ? 'white' : '#334155',
                  fontWeight: '600',
                  fontSize: '0.875rem',
                  cursor: calculatorDisabled ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease',
                  minWidth: '80px',
                  opacity: calculatorDisabled ? 0.5 : 1
                }}
                onMouseEnter={(e) => {
                  if (timeframe !== tf) {
                    e.target.style.background = '#f8fafc';
                    e.target.style.borderColor = '#0891b2';
                  }
                }}
                onMouseLeave={(e) => {
                  if (timeframe !== tf) {
                    e.target.style.background = 'white';
                    e.target.style.borderColor = '#e5e7eb';
                  }
                }}
              >
                {tf === '1h' ? '1 Hour' : tf === '4h' ? '4 Hours' : 'Daily'}
              </button>
            ))}
          </div>
          <p style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.5rem' }}>
            Choose the timeframe for chart analysis and trade calculations
          </p>
        </div>


        <div className="form-section">
          <label className="form-label">Risk Amount (USD)</label>
          <div className="risk-input-container">
            <span className="dollar-sign">$</span>
            <input
              type="number"
              className="risk-input"
              placeholder="Enter amount you want to risk"
              value={riskAmount}
              onChange={(e) => setRiskAmount(e.target.value)}
              data-testid="risk-amount-input"
              disabled={calculatorDisabled}
              style={{
                opacity: calculatorDisabled ? 0.6 : 1,
                cursor: calculatorDisabled ? 'not-allowed' : 'text'
              }}
            />
          </div>
        </div>

        <div className="form-section">
          <label className="form-label">Account Balance (Optional - for risk management)</label>
          <div className="risk-input-container">
            <span className="dollar-sign">$</span>
            <input
              type="number"
              className="risk-input"
              placeholder="Enter your account balance"
              value={accountBalance}
              onChange={(e) => setAccountBalance(e.target.value)}
              data-testid="account-balance-input"
              disabled={calculatorDisabled}
              style={{
                opacity: calculatorDisabled ? 0.6 : 1,
                cursor: calculatorDisabled ? 'not-allowed' : 'text'
              }}
            />
          </div>
          {accountBalance && riskAmount && (
            <div style={{ marginTop: "0.5rem", fontSize: "0.875rem", color: "#64748b" }}>
              Risk: {((parseFloat(riskAmount) / parseFloat(accountBalance)) * 100).toFixed(2)}% of account
            </div>
          )}
        </div>

        <div className="form-section">
          <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "1rem" }}>
            <input
              type="checkbox"
              id="auto-sl"
              checked={enableAutoSL}
              onChange={(e) => setEnableAutoSL(e.target.checked)}
              style={{ width: "1.25rem", height: "1.25rem", cursor: "pointer" }}
              data-testid="enable-auto-sl"
            />
            <label htmlFor="auto-sl" style={{ fontSize: "0.95rem", color: "#334155", fontWeight: "600", cursor: "pointer" }}>
              Enable Auto Stop Loss Adjustment
            </label>
          </div>
          <div style={{ marginBottom: "0.75rem" }}>
            <label className="form-label">Profit Alert Percentage</label>
            <input
              type="number"
              className="risk-input"
              placeholder="Alert when profit reaches %"
              value={profitAlertPercent}
              onChange={(e) => setProfitAlertPercent(parseFloat(e.target.value))}
              min="0.1"
              step="0.1"
              style={{ paddingLeft: "1rem" }}
              data-testid="profit-alert-percent"
            />
          </div>
          <div style={{ fontSize: "0.875rem", color: "#64748b", lineHeight: "1.5", marginBottom: "1.5rem" }}>
            When your trade reaches {profitAlertPercent}% profit, you'll be alerted and stop loss will be moved to breakeven automatically.
          </div>

          {enableAutoSL && (
            <div style={{ marginTop: "1.5rem", padding: "1.5rem", background: "rgba(8, 145, 178, 0.05)", borderRadius: "12px", border: "1px solid rgba(8, 145, 178, 0.2)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
                <h3 style={{ fontSize: "1rem", fontWeight: "600", color: "#334155" }}>Trailing Stop Milestones</h3>
                <button
                  onClick={addTrailingStop}
                  style={{
                    padding: "0.5rem 1rem",
                    background: "linear-gradient(135deg, #0891b2 0%, #06b6d4 100%)",
                    border: "none",
                    borderRadius: "8px",
                    color: "white",
                    fontSize: "0.875rem",
                    fontWeight: "600",
                    cursor: "pointer"
                  }}
                  data-testid="add-milestone-btn"
                >
                  + Add Milestone
                </button>
              </div>
              
              <div style={{ fontSize: "0.875rem", color: "#64748b", marginBottom: "1rem" }}>
                Set profit targets where stop loss will automatically trail to secure gains
              </div>

              {trailingStops.map((milestone, index) => (
                <div
                  key={index}
                  style={{
                    background: "rgba(255, 255, 255, 0.8)",
                    padding: "1rem",
                    borderRadius: "8px",
                    marginBottom: "0.75rem",
                    border: "1px solid rgba(8, 145, 178, 0.2)"
                  }}
                  data-testid={`milestone-${index}`}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem" }}>
                    <span style={{ fontSize: "0.875rem", fontWeight: "600", color: "#334155" }}>
                      Milestone {index + 1}
                    </span>
                    {trailingStops.length > 1 && (
                      <button
                        onClick={() => removeTrailingStop(index)}
                        style={{
                          padding: "0.25rem 0.75rem",
                          background: "rgba(239, 68, 68, 0.1)",
                          border: "1px solid #ef4444",
                          borderRadius: "6px",
                          color: "#b91c1c",
                          fontSize: "0.75rem",
                          fontWeight: "600",
                          cursor: "pointer"
                        }}
                        data-testid={`remove-milestone-${index}`}
                      >
                        Remove
                      </button>
                    )}
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
                    <div>
                      <label style={{ fontSize: "0.75rem", color: "#64748b", display: "block", marginBottom: "0.25rem" }}>
                        When profit reaches % of target
                      </label>
                      <input
                        type="number"
                        value={milestone.profit_target_percent}
                        onChange={(e) => updateTrailingStop(index, "profit_target_percent", e.target.value)}
                        style={{
                          width: "100%",
                          padding: "0.5rem",
                          borderRadius: "6px",
                          border: "1px solid rgba(8, 145, 178, 0.3)",
                          fontSize: "0.875rem"
                        }}
                        min="0"
                        max="100"
                        data-testid={`profit-target-${index}`}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: "0.75rem", color: "#64748b", display: "block", marginBottom: "0.25rem" }}>
                        Move SL to % profit
                      </label>
                      <input
                        type="number"
                        value={milestone.move_sl_to_percent}
                        onChange={(e) => updateTrailingStop(index, "move_sl_to_percent", e.target.value)}
                        style={{
                          width: "100%",
                          padding: "0.5rem",
                          borderRadius: "6px",
                          border: "1px solid rgba(8, 145, 178, 0.3)",
                          fontSize: "0.875rem"
                        }}
                        min="0"
                        data-testid={`sl-position-${index}`}
                      />
                    </div>
                  </div>

                  <div style={{ fontSize: "0.75rem", color: "#0891b2", marginTop: "0.5rem", fontWeight: "500" }}>
                    At {milestone.profit_target_percent}% to target, SL → {milestone.move_sl_to_percent}% profit
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* NEW: Partial Take Profit Controls */}
          <div style={{ marginTop: "1.5rem", padding: "1.5rem", background: "rgba(59, 130, 246, 0.05)", borderRadius: "12px", border: "1px solid rgba(59, 130, 246, 0.2)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "1rem" }}>
              <input
                type="checkbox"
                id="partial-tp"
                checked={enablePartialTP}
                onChange={(e) => setEnablePartialTP(e.target.checked)}
                style={{ width: "1.25rem", height: "1.25rem", cursor: "pointer" }}
              />
              <label htmlFor="partial-tp" style={{ fontSize: "0.95rem", color: "#334155", fontWeight: "600", cursor: "pointer" }}>
                💰 Enable Partial Take Profit
              </label>
            </div>
            
            {enablePartialTP && (
              <>
                <div style={{ fontSize: "0.875rem", color: "#64748b", marginBottom: "1rem", lineHeight: "1.5" }}>
                  Lock in profits while letting the rest run! Close portions of your trade at different levels.
                </div>
                
                {partialTPLevels.map((level, index) => (
                  <div
                    key={index}
                    style={{
                      background: "rgba(255, 255, 255, 0.8)",
                      padding: "1rem",
                      borderRadius: "8px",
                      marginBottom: "0.75rem",
                      border: "1px solid rgba(59, 130, 246, 0.2)"
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem" }}>
                      <span style={{ fontSize: "0.875rem", fontWeight: "600", color: "#334155" }}>
                        TP Level {index + 1}
                      </span>
                      {partialTPLevels.length > 1 && (
                        <button
                          onClick={() => setPartialTPLevels(partialTPLevels.filter((_, i) => i !== index))}
                          style={{
                            padding: "0.25rem 0.75rem",
                            background: "rgba(239, 68, 68, 0.1)",
                            border: "1px solid #ef4444",
                            borderRadius: "6px",
                            color: "#b91c1c",
                            fontSize: "0.75rem",
                            fontWeight: "600",
                            cursor: "pointer"
                          }}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                    
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
                      <div>
                        <label style={{ fontSize: "0.75rem", color: "#64748b", display: "block", marginBottom: "0.25rem" }}>
                          Close % of Position
                        </label>
                        <input
                          type="number"
                          value={level.percent}
                          onChange={(e) => {
                            const updated = [...partialTPLevels];
                            updated[index].percent = parseFloat(e.target.value) || 0;
                            setPartialTPLevels(updated);
                          }}
                          style={{
                            width: "100%",
                            padding: "0.5rem",
                            borderRadius: "6px",
                            border: "1px solid rgba(59, 130, 246, 0.3)",
                            fontSize: "0.875rem"
                          }}
                          min="1"
                          max="100"
                          step="5"
                        />
                      </div>
                      <div>
                        <label style={{ fontSize: "0.75rem", color: "#64748b", display: "block", marginBottom: "0.25rem" }}>
                          At Risk/Reward Ratio
                        </label>
                        <input
                          type="number"
                          value={level.ratio}
                          onChange={(e) => {
                            const updated = [...partialTPLevels];
                            updated[index].ratio = parseFloat(e.target.value) || 1;
                            setPartialTPLevels(updated);
                          }}
                          style={{
                            width: "100%",
                            padding: "0.5rem",
                            borderRadius: "6px",
                            border: "1px solid rgba(59, 130, 246, 0.3)",
                            fontSize: "0.875rem"
                          }}
                          min="0.5"
                          max="5"
                          step="0.1"
                        />
                      </div>
                    </div>
                    
                    <div style={{ fontSize: "0.75rem", color: "#3b82f6", marginTop: "0.5rem", fontWeight: "500" }}>
                      Close {level.percent}% at {level.ratio}R (e.g., if risk is $100, take profit at ${level.ratio * 100})
                    </div>
                  </div>
                ))}
                
                <button
                  onClick={() => setPartialTPLevels([...partialTPLevels, { percent: 20, ratio: 2.5 }])}
                  style={{
                    padding: "0.5rem 1rem",
                    background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
                    border: "none",
                    borderRadius: "8px",
                    color: "white",
                    fontSize: "0.875rem",
                    fontWeight: "600",
                    cursor: "pointer",
                    width: "100%"
                  }}
                >
                  + Add Another TP Level
                </button>
              </>
            )}
          </div>
        </div>

        <button
          className={`calculate-btn ${selectedDirection ? 'activate-mode' : ''}`}
          onClick={selectedDirection ? handleActivateFromOpportunity : handleCalculate}
          disabled={loading || calculatorDisabled}
          data-testid={selectedDirection ? "activate-trade-btn" : "calculate-btn"}
          style={{
            opacity: calculatorDisabled ? 0.5 : 1,
            cursor: calculatorDisabled ? 'not-allowed' : 'pointer',
            background: calculatorDisabled 
              ? '#6b7280' 
              : selectedDirection 
                ? 'linear-gradient(135deg, #10b981, #059669)' 
                : undefined,
            boxShadow: selectedDirection ? '0 4px 15px rgba(16, 185, 129, 0.4)' : undefined
          }}
        >
          {calculatorDisabled 
            ? "🛑 Calculator Disabled (Rollover Protection)" 
            : loading 
              ? "Processing..." 
              : selectedDirection 
                ? `🚀 Activate ${selectedDirection} Trade` 
                : "Calculate Trade"}
        </button>

        {error && (
          <div 
            className={`error-message ${error.includes('subscription') || error.includes('API limit') ? 'error-critical' : ''}`}
            data-testid="error-message"
            style={{
              backgroundColor: error.includes('subscription') || error.includes('API limit') ? '#dc2626' : '#ef4444',
              color: 'white',
              padding: '16px',
              borderRadius: '8px',
              marginTop: '16px',
              fontWeight: error.includes('subscription') || error.includes('API limit') ? 'bold' : 'normal'
            }}
          >
            {error.includes('subscription') || error.includes('API limit') ? '⚠️ CRITICAL: ' : ''}
            {error}
          </div>
        )}
      </div>
      )}

      {/* Result cards - only show after chart confirmation */}
      {result && !showChart && (
        <>
          {/* 🔄 CURRENCY EXPOSURE CONFLICT WARNING */}
          {result.currency_conflict && result.currency_conflict.has_conflict && (
            <div style={{
              background: result.currency_conflict.severity === "HIGH" 
                ? "linear-gradient(135deg, rgba(239, 68, 68, 0.15) 0%, rgba(220, 38, 38, 0.15) 100%)"
                : "linear-gradient(135deg, rgba(251, 191, 36, 0.15) 0%, rgba(245, 158, 11, 0.15) 100%)",
              border: `3px solid ${result.currency_conflict.severity === "HIGH" ? "#ef4444" : "#f59e0b"}`,
              borderRadius: "16px",
              padding: "1.25rem",
              marginBottom: "1.5rem"
            }} data-testid="currency-conflict-warning">
              <div style={{ 
                display: "flex", 
                alignItems: "flex-start", 
                gap: "1rem"
              }}>
                <div style={{ 
                  fontSize: "2rem",
                  lineHeight: "1"
                }}>
                  {result.currency_conflict.severity === "HIGH" ? "🚨" : "📊"}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ 
                    fontWeight: "700", 
                    fontSize: "1.1rem", 
                    color: result.currency_conflict.severity === "HIGH" ? "#dc2626" : "#d97706",
                    marginBottom: "0.5rem"
                  }}>
                    {result.currency_conflict.severity === "HIGH" ? "CORRELATED TRADE WARNING" : "POSITION OVERLAP DETECTED"}
                  </div>
                  <div style={{ 
                    fontSize: "0.95rem", 
                    color: "#374151",
                    marginBottom: "0.75rem",
                    lineHeight: "1.5"
                  }}>
                    {result.currency_conflict.summary}
                  </div>
                  <div style={{
                    background: "rgba(255, 255, 255, 0.7)",
                    padding: "0.75rem",
                    borderRadius: "8px",
                    fontSize: "0.85rem",
                    color: "#4b5563"
                  }}>
                    <div style={{ fontWeight: "600", marginBottom: "0.5rem" }}>Your Exposure:</div>
                    <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
                      <span style={{ 
                        background: "#dcfce7", 
                        color: "#166534", 
                        padding: "0.25rem 0.5rem", 
                        borderRadius: "4px",
                        fontWeight: "600"
                      }}>
                        📈 LONG {result.currency_conflict.new_exposure?.long}
                      </span>
                      <span style={{ 
                        background: "#fee2e2", 
                        color: "#991b1b", 
                        padding: "0.25rem 0.5rem", 
                        borderRadius: "4px",
                        fontWeight: "600"
                      }}>
                        📉 SHORT {result.currency_conflict.new_exposure?.short}
                      </span>
                    </div>
                    {result.currency_conflict.conflicts && result.currency_conflict.conflicts.length > 0 && (
                      <div style={{ marginTop: "0.75rem", fontSize: "0.8rem" }}>
                        <div style={{ fontWeight: "600", marginBottom: "0.25rem" }}>Conflicting with:</div>
                        {result.currency_conflict.conflicts.map((conflict, idx) => (
                          <div key={idx} style={{ 
                            color: conflict.type.includes("DOUBLE") ? "#dc2626" : "#6b7280",
                            marginLeft: "0.5rem"
                          }}>
                            • {conflict.existing_trade}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div style={{
                    marginTop: "0.75rem",
                    fontSize: "0.85rem",
                    color: result.currency_conflict.severity === "HIGH" ? "#dc2626" : "#d97706",
                    fontStyle: "italic"
                  }}>
                    💡 {result.currency_conflict.recommendation}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="forex-card">
          {/* COMPACT SUMMARY CARD */}
          <div style={{
            background: "linear-gradient(135deg, rgba(8, 145, 178, 0.1) 0%, rgba(6, 182, 212, 0.1) 100%)",
            border: "3px solid #0891b2",
            borderRadius: "16px",
            padding: "1.5rem",
            marginBottom: "1.5rem"
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
              <div style={{ display: "flex", gap: "1rem", alignItems: "center" }}>
                <span className={`trade-direction ${result.direction.toLowerCase()}`} style={{
                  padding: "0.5rem 1.25rem",
                  borderRadius: "8px",
                  fontSize: "1.25rem",
                  fontWeight: "700"
                }} data-testid="trade-direction">
                  {result.direction}
                </span>
                <span style={{
                  padding: "0.5rem 1rem",
                  borderRadius: "8px",
                  fontSize: "1.25rem",
                  fontWeight: "700",
                  background: (result.final_probability || result.probability) >= 70 
                    ? "rgba(16, 185, 129, 0.2)" 
                    : "rgba(251, 191, 36, 0.2)",
                  color: (result.final_probability || result.probability) >= 70 
                    ? "#059669" 
                    : "#f59e0b",
                  border: `2px solid ${(result.final_probability || result.probability) >= 70 ? "#10b981" : "#fbbf24"}`,
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem"
                }} data-testid="probability-badge">
                  <span style={{ fontSize: "0.9rem", opacity: 0.8 }}>Confidence:</span>
                  <strong>{result.final_probability ? result.final_probability : result.probability}%</strong>
                  {(result.final_probability || result.probability) < 70 && (
                    <span style={{ fontSize: "0.75rem", marginLeft: "0.25rem" }}>⚠️ Experimental</span>
                  )}
                </span>
                
                {/* Historical Win Rate Badge */}
                {result.historical_performance && result.historical_performance.win_rate !== null && (
                  <span style={{
                    padding: "0.5rem 1rem",
                    borderRadius: "8px",
                    fontSize: "1rem",
                    fontWeight: "700",
                    background: result.historical_performance.warning 
                      ? "rgba(239, 68, 68, 0.15)" 
                      : "rgba(16, 185, 129, 0.15)",
                    color: result.historical_performance.warning 
                      ? "#dc2626" 
                      : "#059669",
                    border: `2px solid ${result.historical_performance.warning ? "#ef4444" : "#10b981"}`,
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem"
                  }} data-testid="historical-win-rate-badge" title={result.historical_performance.message}>
                    {result.historical_performance.warning && <span style={{ fontSize: "1.1rem" }}>⚠️</span>}
                    <span>📊 {result.historical_performance.win_rate}% Win Rate</span>
                    <span style={{ fontSize: "0.75rem", opacity: 0.8 }}>({result.historical_performance.sample_size})</span>
                  </span>
                )}
              </div>
              {result.price_action && (
                <div style={{
                  padding: "0.5rem 1rem",
                  background: result.price_action.context.includes("Enter Now")
                    ? "rgba(34, 197, 94, 0.2)"
                    : result.price_action.context === "Neutral Zone" || result.price_action.context.includes("Set Limit")
                    ? "rgba(59, 130, 246, 0.2)"
                    : "rgba(251, 191, 36, 0.2)",
                  border: `2px solid ${
                    result.price_action.context.includes("Enter Now")
                      ? "#22c55e"
                      : result.price_action.context === "Neutral Zone" || result.price_action.context.includes("Set Limit")
                      ? "#3b82f6"
                      : "#fbbf24"
                  }`,
                  borderRadius: "8px",
                  fontSize: "0.875rem",
                  fontWeight: "700",
                  color: result.price_action.context.includes("Enter Now")
                    ? "#16a34a"
                    : result.price_action.context === "Neutral Zone" || result.price_action.context.includes("Set Limit")
                    ? "#3b82f6"
                    : "#f59e0b"
                }}>
                  {result.price_action.context.includes("Enter Now") && "✅ ENTER NOW"}
                  {result.price_action.context === "Neutral Zone" && "📋 SET LIMIT"}
                  {result.price_action.context.includes("Set Limit") && "🎯 SET LIMIT"}
                  {(result.price_action.context === "Overextended" || result.price_action.context === "Pulling Back") && "⏳ WAIT"}
                </div>
              )}
            </div>

            {/* COMPACT GRID - Key Info Only */}
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
              gap: "1rem"
            }}>
              <div style={{ background: "rgba(255, 255, 255, 0.8)", padding: "1rem", borderRadius: "10px", textAlign: "center" }}>
                <div style={{ fontSize: "0.75rem", color: "#64748b", marginBottom: "0.25rem" }}>Entry</div>
                <div style={{ fontSize: "1.25rem", fontWeight: "700", color: "#0f172a" }} data-testid="entry-price">{result.entry_price}</div>
              </div>
              <div style={{ background: "rgba(255, 255, 255, 0.8)", padding: "1rem", borderRadius: "10px", textAlign: "center" }}>
                <div style={{ fontSize: "0.75rem", color: "#64748b", marginBottom: "0.25rem" }}>Stop Loss</div>
                <div style={{ fontSize: "1.25rem", fontWeight: "700", color: "#ef4444" }} data-testid="stop-loss">
                  {result.stop_loss}
                  {calculateHalfSL(result.entry_price, result.stop_loss, result.pair) && (
                    <span style={{ fontSize: "0.9rem", color: "#f97316", marginLeft: "0.25rem" }}>
                      ({calculateHalfSL(result.entry_price, result.stop_loss, result.pair)})
                    </span>
                  )}
                </div>
              </div>
              <div style={{ background: "rgba(255, 255, 255, 0.8)", padding: "1rem", borderRadius: "10px", textAlign: "center" }}>
                <div style={{ fontSize: "0.75rem", color: "#64748b", marginBottom: "0.25rem" }}>Take Profit</div>
                <div style={{ fontSize: "1.25rem", fontWeight: "700", color: "#10b981" }} data-testid="take-profit">{result.take_profit}</div>
              </div>
              <div style={{ background: "rgba(255, 255, 255, 0.8)", padding: "1rem", borderRadius: "10px", textAlign: "center" }}>
                <div style={{ fontSize: "0.75rem", color: "#64748b", marginBottom: "0.25rem" }}>Lot Size</div>
                <div style={{ fontSize: "1.25rem", fontWeight: "700", color: "#0f172a" }} data-testid="lot-size">{result.lot_size}</div>
              </div>
              <div style={{ background: "rgba(255, 255, 255, 0.8)", padding: "1rem", borderRadius: "10px", textAlign: "center" }}>
                <div style={{ fontSize: "0.75rem", color: "#64748b", marginBottom: "0.25rem" }}>Risk</div>
                <div style={{ fontSize: "1.25rem", fontWeight: "700", color: "#ef4444" }} data-testid="risk-amount">${result.risk_amount}</div>
              </div>
              <div style={{ background: "rgba(255, 255, 255, 0.8)", padding: "1rem", borderRadius: "10px", textAlign: "center" }}>
                <div style={{ fontSize: "0.75rem", color: "#64748b", marginBottom: "0.25rem" }}>Reward</div>
                <div style={{ fontSize: "1.25rem", fontWeight: "700", color: "#10b981" }} data-testid="potential-profit">${result.potential_profit}</div>
              </div>
            </div>
          </div>

            {/* Volume Analysis Section - COLLAPSIBLE */}
            {loadingVolume && (
              <div style={{ marginTop: "1rem", textAlign: "center", color: "#0891b2", fontSize: "0.875rem" }}>
                Loading volume analysis...
              </div>
            )}

            {volumeAnalysis && (
              <div style={{ marginTop: "1rem" }}>
                <button
                  onClick={() => toggleSection('volume')}
                  style={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "1rem",
                    background: "rgba(59, 130, 246, 0.1)",
                    border: "2px solid #3b82f6",
                    borderRadius: "10px",
                    cursor: "pointer",
                    transition: "all 0.3s ease"
                  }}
                >
                  <span style={{ fontSize: "1rem", fontWeight: "700", color: "#3b82f6" }}>
                    📊 Volume Analysis
                  </span>
                  <span style={{ fontSize: "1.25rem", color: "#3b82f6" }}>
                    {expandedSections.volume ? "−" : "+"}
                  </span>
                </button>
                
                {expandedSections.volume && (
                  <div style={{ marginTop: "0.75rem", padding: "1rem", background: "rgba(59, 130, 246, 0.05)", borderRadius: "10px" }}>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "0.75rem" }}>
                      {/* 60 Minutes */}
                      <div style={{
                        background: volumeAnalysis.volume_60min.sentiment === "BULLISH" 
                          ? "rgba(16, 185, 129, 0.1)" 
                          : volumeAnalysis.volume_60min.sentiment === "BEARISH"
                          ? "rgba(239, 68, 68, 0.1)"
                          : "rgba(148, 163, 184, 0.1)",
                        padding: "1rem",
                        borderRadius: "8px",
                        border: `2px solid ${
                          volumeAnalysis.volume_60min.sentiment === "BULLISH" 
                            ? "#10b981" 
                            : volumeAnalysis.volume_60min.sentiment === "BEARISH"
                            ? "#ef4444"
                            : "#94a3b8"
                        }`
                      }} data-testid="volume-60min">
                        <div style={{ fontSize: "0.75rem", color: "#64748b", marginBottom: "0.25rem" }}>60 Min</div>
                        <div style={{ fontSize: "1.125rem", fontWeight: "700", color: 
                          volumeAnalysis.volume_60min.sentiment === "BULLISH" ? "#10b981" :
                          volumeAnalysis.volume_60min.sentiment === "BEARISH" ? "#ef4444" : "#64748b"
                        }}>
                          {volumeAnalysis.volume_60min.sentiment}
                        </div>
                        <div style={{ fontSize: "0.75rem", color: "#334155" }}>
                          {volumeAnalysis.volume_60min.strength}% Strength
                        </div>
                      </div>

                      {/* 30 Minutes */}
                      <div style={{
                        background: volumeAnalysis.volume_30min.sentiment === "BULLISH" 
                          ? "rgba(16, 185, 129, 0.1)" 
                          : volumeAnalysis.volume_30min.sentiment === "BEARISH"
                          ? "rgba(239, 68, 68, 0.1)"
                          : "rgba(148, 163, 184, 0.1)",
                        padding: "1rem",
                        borderRadius: "8px",
                        border: `2px solid ${
                          volumeAnalysis.volume_30min.sentiment === "BULLISH" 
                            ? "#10b981" 
                            : volumeAnalysis.volume_30min.sentiment === "BEARISH"
                            ? "#ef4444"
                            : "#94a3b8"
                        }`
                      }} data-testid="volume-30min">
                        <div style={{ fontSize: "0.75rem", color: "#64748b", marginBottom: "0.25rem" }}>30 Min</div>
                        <div style={{ fontSize: "1.125rem", fontWeight: "700", color: 
                          volumeAnalysis.volume_30min.sentiment === "BULLISH" ? "#10b981" :
                          volumeAnalysis.volume_30min.sentiment === "BEARISH" ? "#ef4444" : "#64748b"
                        }}>
                          {volumeAnalysis.volume_30min.sentiment}
                        </div>
                        <div style={{ fontSize: "0.75rem", color: "#334155" }}>
                          {volumeAnalysis.volume_30min.strength}% Strength
                        </div>
                      </div>

                      {/* 15 Minutes */}
                      <div style={{
                        background: volumeAnalysis.volume_15min.sentiment === "BULLISH" 
                          ? "rgba(16, 185, 129, 0.1)" 
                          : volumeAnalysis.volume_15min.sentiment === "BEARISH"
                          ? "rgba(239, 68, 68, 0.1)"
                          : "rgba(148, 163, 184, 0.1)",
                        padding: "1rem",
                        borderRadius: "8px",
                        border: `2px solid ${
                          volumeAnalysis.volume_15min.sentiment === "BULLISH" 
                            ? "#10b981" 
                            : volumeAnalysis.volume_15min.sentiment === "BEARISH"
                            ? "#ef4444"
                            : "#94a3b8"
                        }`
                      }} data-testid="volume-15min">
                        <div style={{ fontSize: "0.75rem", color: "#64748b", marginBottom: "0.25rem" }}>15 Min</div>
                        <div style={{ fontSize: "1.125rem", fontWeight: "700", color: 
                          volumeAnalysis.volume_15min.sentiment === "BULLISH" ? "#10b981" :
                          volumeAnalysis.volume_15min.sentiment === "BEARISH" ? "#ef4444" : "#64748b"
                        }}>
                          {volumeAnalysis.volume_15min.sentiment}
                        </div>
                        <div style={{ fontSize: "0.75rem", color: "#334155" }}>
                          {volumeAnalysis.volume_15min.strength}% Strength
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}


            {/* Technical Indicators Section - COLLAPSIBLE */}
            {result.rsi && (
              <div style={{ marginTop: "1rem" }}>
                <button
                  onClick={() => toggleSection('technical')}
                  style={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "1rem",
                    background: "rgba(168, 85, 247, 0.1)",
                    border: "2px solid #a855f7",
                    borderRadius: "10px",
                    cursor: "pointer",
                    transition: "all 0.3s ease"
                  }}
                >
                  <span style={{ fontSize: "1rem", fontWeight: "700", color: "#a855f7" }}>
                    📈 Technical Analysis
                  </span>
                  <span style={{ fontSize: "1.25rem", color: "#a855f7" }}>
                    {expandedSections.technical ? "−" : "+"}
                  </span>
                </button>
                
                {expandedSections.technical && (
                <div style={{ marginTop: "0.75rem", padding: "1rem", background: "rgba(168, 85, 247, 0.05)", borderRadius: "10px" }}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "1rem" }}>
                  {/* RSI */}
                  <div style={{
                    background: result.rsi > 70 ? "rgba(239, 68, 68, 0.1)" : result.rsi < 30 ? "rgba(16, 185, 129, 0.1)" : "rgba(148, 163, 184, 0.1)",
                    padding: "1.25rem",
                    borderRadius: "12px",
                    border: `2px solid ${result.rsi > 70 ? "#ef4444" : result.rsi < 30 ? "#10b981" : "#94a3b8"}`
                  }}>
                    <div style={{ fontSize: "0.875rem", fontWeight: "600", color: "#64748b", marginBottom: "0.5rem" }}>
                      RSI (14)
                    </div>
                    <div style={{ fontSize: "1.5rem", fontWeight: "700", color: result.rsi > 70 ? "#ef4444" : result.rsi < 30 ? "#10b981" : "#64748b" }}>
                      {result.rsi}
                    </div>
                    <div style={{ fontSize: "0.875rem", color: "#334155", marginTop: "0.25rem" }}>
                      {result.rsi > 70 ? "Overbought" : result.rsi < 30 ? "Oversold" : "Neutral"}
                    </div>
                  </div>

                  {/* 20-Day MA */}
                  <div style={{
                    background: "rgba(59, 130, 246, 0.1)",
                    padding: "1.25rem",
                    borderRadius: "12px",
                    border: "2px solid #3b82f6"
                  }}>
                    <div style={{ fontSize: "0.875rem", fontWeight: "600", color: "#64748b", marginBottom: "0.5rem" }}>
                      20-Day MA
                    </div>
                    <div style={{ fontSize: "1.5rem", fontWeight: "700", color: "#3b82f6" }}>
                      {result.ma_20}
                    </div>
                    <div style={{ fontSize: "0.875rem", color: "#334155", marginTop: "0.25rem" }}>
                      {result.entry_price > result.ma_20 ? "Price Above (Bullish)" : "Price Below (Bearish)"}
                    </div>
                  </div>

                  {/* Support/Resistance */}
                  {result.support_resistance && (
                    <div style={{
                      background: "rgba(168, 85, 247, 0.1)",
                      padding: "1.25rem",
                      borderRadius: "12px",
                      border: "2px solid #a855f7"
                    }}>
                      <div style={{ fontSize: "0.875rem", fontWeight: "600", color: "#64748b", marginBottom: "0.5rem" }}>
                        Support/Resistance
                      </div>
                      <div style={{ fontSize: "0.875rem", color: "#334155" }}>
                        Support: {result.support_resistance.support}
                      </div>
                      <div style={{ fontSize: "0.875rem", color: "#334155" }}>
                        Resistance: {result.support_resistance.resistance}
                      </div>
                      <div style={{ fontSize: "0.75rem", color: "#64748b", marginTop: "0.5rem" }}>
                        Distance: R {result.support_resistance.dist_to_resistance_pct}% | S {result.support_resistance.dist_to_support_pct}%
                      </div>
                    </div>
                  )}

                  {/* Market Session */}
                  {result.market_session && (
                    <div style={{
                      background: "rgba(34, 197, 94, 0.1)",
                      padding: "1.25rem",
                      borderRadius: "12px",
                      border: "2px solid #22c55e"
                    }}>
                      <div style={{ fontSize: "0.875rem", fontWeight: "600", color: "#64748b", marginBottom: "0.5rem" }}>
                        Market Session
                      </div>
                      <div style={{ fontSize: "1.25rem", fontWeight: "700", color: "#22c55e" }}>
                        {result.market_session}
                      </div>
                      <div style={{ fontSize: "0.75rem", color: "#334155", marginTop: "0.25rem" }}>
                        {result.market_session.includes("High Liquidity") ? "⚡ Best Trading Time" : "Active"}
                      </div>
                    </div>
                  )}

                  {/* News Risk */}
                  {result.news_risk && (
                    <div style={{
                      background: result.news_risk.news_risk === "HIGH" || result.news_risk.news_risk === "VERY HIGH" 
                        ? "rgba(239, 68, 68, 0.1)" 
                        : "rgba(34, 197, 94, 0.1)",
                      padding: "1.25rem",
                      borderRadius: "12px",
                      border: `2px solid ${result.news_risk.news_risk === "HIGH" || result.news_risk.news_risk === "VERY HIGH" ? "#ef4444" : "#22c55e"}`
                    }}>
                      <div style={{ fontSize: "0.875rem", fontWeight: "600", color: "#64748b", marginBottom: "0.5rem" }}>
                        News Risk
                      </div>
                      <div style={{ 
                        fontSize: "1.25rem", 
                        fontWeight: "700", 
                        color: result.news_risk.news_risk === "HIGH" || result.news_risk.news_risk === "VERY HIGH" ? "#ef4444" : "#22c55e"
                      }}>
                        {result.news_risk.news_risk}
                      </div>
                      <div style={{ fontSize: "0.75rem", color: "#334155", marginTop: "0.25rem" }}>
                        {result.news_risk.note}
                      </div>
                    </div>
                  )}

                  {/* 📰 Smart News Alignment - Shows when trade aligns with upcoming news */}
                  {result.news_alignment && result.news_alignment.has_upcoming_news && (
                    <div style={{
                      background: "rgba(59, 130, 246, 0.15)",
                      padding: "1.25rem",
                      borderRadius: "12px",
                      border: "2px solid #3b82f6",
                      position: "relative",
                      overflow: "hidden"
                    }} data-testid="news-alignment-card">
                      <div style={{
                        position: "absolute",
                        top: "-5px",
                        right: "-5px",
                        width: "60px",
                        height: "60px",
                        background: "rgba(59, 130, 246, 0.2)",
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "1.5rem"
                      }}>
                        📰
                      </div>
                      <div style={{ fontSize: "0.875rem", fontWeight: "600", color: "#1d4ed8", marginBottom: "0.5rem" }}>
                        ✨ NEWS ALIGNED TRADE
                      </div>
                      {result.news_alignment.aligned_event && (
                        <>
                          <div style={{ 
                            fontSize: "1rem", 
                            fontWeight: "700", 
                            color: "#1e40af",
                            marginBottom: "0.5rem"
                          }}>
                            {result.news_alignment.aligned_event.event}
                          </div>
                          <div style={{ 
                            display: "flex", 
                            gap: "0.75rem", 
                            flexWrap: "wrap",
                            fontSize: "0.75rem",
                            color: "#1e3a8a"
                          }}>
                            <span style={{ background: "rgba(59, 130, 246, 0.2)", padding: "0.25rem 0.5rem", borderRadius: "4px" }}>
                              ⏰ In {result.news_alignment.aligned_event.minutes_until} mins
                            </span>
                            <span style={{ background: "rgba(59, 130, 246, 0.2)", padding: "0.25rem 0.5rem", borderRadius: "4px" }}>
                              💱 {result.news_alignment.aligned_event.currency}
                            </span>
                            <span style={{ 
                              background: result.news_alignment.aligned_event.expected_impact === "positive" 
                                ? "rgba(34, 197, 94, 0.2)" 
                                : result.news_alignment.aligned_event.expected_impact === "negative"
                                ? "rgba(239, 68, 68, 0.2)"
                                : "rgba(251, 191, 36, 0.2)", 
                              padding: "0.25rem 0.5rem", 
                              borderRadius: "4px" 
                            }}>
                              📊 {result.news_alignment.aligned_event.expected_impact?.toUpperCase() || "NEUTRAL"}
                            </span>
                          </div>
                          <div style={{ 
                            fontSize: "0.75rem", 
                            color: "#334155", 
                            marginTop: "0.75rem",
                            background: "rgba(255,255,255,0.5)",
                            padding: "0.5rem",
                            borderRadius: "6px"
                          }}>
                            💡 {result.news_alignment.aligned_event.reason}
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {/* Market Sentiment (Correlation Check) */}
                  {result.correlation_check && result.correlation_check.status && (
                    <div style={{
                      background: result.correlation_check.color === "green" 
                        ? "rgba(34, 197, 94, 0.1)" 
                        : result.correlation_check.color === "yellow"
                        ? "rgba(251, 191, 36, 0.1)"
                        : result.correlation_check.color === "red"
                        ? "rgba(239, 68, 68, 0.1)"
                        : "rgba(148, 163, 184, 0.1)",
                      padding: "1.25rem",
                      borderRadius: "12px",
                      border: `2px solid ${
                        result.correlation_check.color === "green" 
                          ? "#22c55e" 
                          : result.correlation_check.color === "yellow"
                          ? "#fbbf24"
                          : result.correlation_check.color === "red"
                          ? "#ef4444"
                          : "#94a3b8"
                      }`
                    }}>
                      <div style={{ fontSize: "0.875rem", fontWeight: "600", color: "#64748b", marginBottom: "0.5rem" }}>
                        Market Sentiment {result.correlation_check.status === "strong" && "⚡"}
                      </div>
                      <div style={{ 
                        fontSize: "1.25rem", 
                        fontWeight: "700", 
                        color: result.correlation_check.color === "green" 
                          ? "#22c55e" 
                          : result.correlation_check.color === "yellow"
                          ? "#fbbf24"
                          : result.correlation_check.color === "red"
                          ? "#ef4444"
                          : "#64748b"
                      }}>
                        {result.correlation_check.sentiment}
                      </div>
                      <div style={{ fontSize: "0.75rem", color: "#334155", marginTop: "0.5rem" }}>
                        {result.correlation_check.agreeing_pairs}/{result.correlation_check.total_pairs} pairs agree
                      </div>
                      {result.correlation_check.details && result.correlation_check.details.length > 0 && (
                        <div style={{ fontSize: "0.75rem", color: "#64748b", marginTop: "0.5rem", borderTop: "1px solid rgba(0,0,0,0.1)", paddingTop: "0.5rem" }}>
                          {result.correlation_check.details.map((detail, idx) => (
                            <div key={idx} style={{ marginBottom: "0.25rem" }}>
                              {detail.pair}: {detail.direction} {detail.agrees ? "✓" : "✗"}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}


                </div>
                </div>
                )}
              </div>
            )}

            {/* 24-Hour Currency Strength - COLLAPSIBLE */}
            {result.currency_strength && result.currency_strength.scores && (
              <div style={{ marginTop: "1rem" }}>
                <button
                  onClick={() => toggleSection('currencyStrength')}
                  style={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "1rem",
                    background: result.royal_flush 
                      ? "linear-gradient(135deg, rgba(234, 179, 8, 0.2) 0%, rgba(251, 191, 36, 0.2) 100%)"
                      : "rgba(139, 92, 246, 0.1)",
                    border: result.royal_flush ? "3px solid #eab308" : "2px solid #8b5cf6",
                    borderRadius: "10px",
                    cursor: "pointer",
                    transition: "all 0.3s ease"
                  }}
                >
                  <span style={{ fontSize: "1rem", fontWeight: "700", color: result.royal_flush ? "#eab308" : "#8b5cf6" }}>
                    💪 Currency Strength {result.royal_flush && "🎰"}
                  </span>
                  <span style={{ fontSize: "1.25rem", color: result.royal_flush ? "#eab308" : "#8b5cf6" }}>
                    {expandedSections.currencyStrength ? "−" : "+"}
                  </span>
                </button>
                
                {expandedSections.currencyStrength && (
                  <div style={{ marginTop: "0.75rem", padding: "1rem", background: "rgba(139, 92, 246, 0.05)", borderRadius: "10px" }}>
                    <div 
                      className={result.royal_flush ? "royal-flush-flash" : ""}
                      style={{
                      background: result.royal_flush 
                        ? "linear-gradient(135deg, rgba(234, 179, 8, 0.2) 0%, rgba(251, 191, 36, 0.2) 100%)"
                        : "rgba(139, 92, 246, 0.1)",
                      padding: "1.5rem",
                      borderRadius: "12px",
                      border: result.royal_flush 
                        ? "3px solid #eab308"
                        : "2px solid #8b5cf6",
                      gridColumn: "1 / -1"
                    }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1.5rem" }}>
                        <div style={{ fontSize: "1rem", fontWeight: "700", color: "#0f172a" }}>
                          24-Hour Currency Strength {result.royal_flush && "🎰"}
                        </div>
                        {result.royal_flush && (
                          <div style={{ 
                            fontSize: "0.875rem", 
                            fontWeight: "700", 
                            color: "#eab308",
                            background: "rgba(234, 179, 8, 0.2)",
                            padding: "0.5rem 1rem",
                            borderRadius: "999px",
                            border: "2px solid #eab308"
                          }}>
                            ROYAL FLUSH ⚡
                          </div>
                        )}
                      </div>
                      
                      {(() => {
                        const baseCurrency = result.pair.substring(0, 3);
                        const quoteCurrency = result.pair.substring(3, 6);
                        const baseStrength = result.currency_strength.scores[baseCurrency] || 50;
                        const quoteStrength = result.currency_strength.scores[quoteCurrency] || 50;
                        
                        return (
                          <div style={{ 
                            display: "grid", 
                            gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", 
                            gap: "1.5rem",
                            width: "100%"
                          }}>
                            {/* Base Currency */}
                            <div style={{
                              padding: "1.5rem",
                              background: baseStrength > 60 
                                ? "rgba(34, 197, 94, 0.1)" 
                                : baseStrength < 40 
                                ? "rgba(239, 68, 68, 0.1)" 
                                : "rgba(148, 163, 184, 0.1)",
                              borderRadius: "12px",
                              border: `2px solid ${baseStrength > 60 ? "#22c55e" : baseStrength < 40 ? "#ef4444" : "#94a3b8"}`
                            }}>
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
                                <div>
                                  <div style={{ fontSize: "0.75rem", fontWeight: "600", color: "#64748b", marginBottom: "0.25rem" }}>
                                    BASE CURRENCY
                                  </div>
                                  <div style={{ fontSize: "2rem", fontWeight: "700", color: "#0f172a" }}>
                                    {baseCurrency}
                                  </div>
                                </div>
                                <div style={{ 
                                  fontSize: "2.5rem", 
                                  fontWeight: "700",
                                  color: baseStrength > 60 ? "#22c55e" : baseStrength < 40 ? "#ef4444" : "#64748b"
                                }}>
                                  {baseStrength.toFixed(0)}
                                </div>
                              </div>
                              
                              <div style={{
                                width: "100%",
                                height: "12px",
                                background: "rgba(0, 0, 0, 0.1)",
                                borderRadius: "999px",
                                overflow: "hidden",
                                marginBottom: "0.75rem"
                              }}>
                                <div style={{
                                  width: `${baseStrength}%`,
                                  height: "100%",
                                  background: baseStrength > 60 
                                    ? "linear-gradient(90deg, #22c55e 0%, #16a34a 100%)"
                                    : baseStrength < 40
                                    ? "linear-gradient(90deg, #ef4444 0%, #dc2626 100%)"
                                    : "linear-gradient(90deg, #64748b 0%, #475569 100%)",
                                  borderRadius: "999px",
                                  transition: "width 0.3s ease"
                                }}>
                                </div>
                              </div>
                              
                              <div style={{ fontSize: "0.875rem", fontWeight: "600", color: "#334155" }}>
                                {baseStrength > 60 ? "🟢 Strong" : baseStrength < 40 ? "🔴 Weak" : "⚪ Neutral"}
                              </div>
                            </div>

                            {/* Quote Currency */}
                            <div style={{
                              padding: "1.5rem",
                              background: quoteStrength > 60 
                                ? "rgba(34, 197, 94, 0.1)" 
                                : quoteStrength < 40 
                                ? "rgba(239, 68, 68, 0.1)" 
                                : "rgba(148, 163, 184, 0.1)",
                              borderRadius: "12px",
                              border: `2px solid ${quoteStrength > 60 ? "#22c55e" : quoteStrength < 40 ? "#ef4444" : "#94a3b8"}`
                            }}>
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
                                <div>
                                  <div style={{ fontSize: "0.75rem", fontWeight: "600", color: "#64748b", marginBottom: "0.25rem" }}>
                                    QUOTE CURRENCY
                                  </div>
                                  <div style={{ fontSize: "2rem", fontWeight: "700", color: "#0f172a" }}>
                                    {quoteCurrency}
                                  </div>
                                </div>
                                <div style={{ 
                                  fontSize: "2.5rem", 
                                  fontWeight: "700",
                                  color: quoteStrength > 60 ? "#22c55e" : quoteStrength < 40 ? "#ef4444" : "#64748b"
                                }}>
                                  {quoteStrength.toFixed(0)}
                                </div>
                              </div>
                              
                              <div style={{
                                width: "100%",
                                height: "12px",
                                background: "rgba(0, 0, 0, 0.1)",
                                borderRadius: "999px",
                                overflow: "hidden",
                                marginBottom: "0.75rem"
                              }}>
                                <div style={{
                                  width: `${quoteStrength}%`,
                                  height: "100%",
                                  background: quoteStrength > 60 
                                    ? "linear-gradient(90deg, #22c55e 0%, #16a34a 100%)"
                                    : quoteStrength < 40
                                    ? "linear-gradient(90deg, #ef4444 0%, #dc2626 100%)"
                                    : "linear-gradient(90deg, #64748b 0%, #475569 100%)",
                                  borderRadius: "999px",
                                  transition: "width 0.3s ease"
                                }}>
                                </div>
                              </div>
                              
                              <div style={{ fontSize: "0.875rem", fontWeight: "600", color: "#334155" }}>
                                {quoteStrength > 60 ? "🟢 Strong" : quoteStrength < 40 ? "🔴 Weak" : "⚪ Neutral"}
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                      
                      {result.royal_flush && (
                        <div style={{
                          marginTop: "1rem",
                          padding: "1rem",
                          background: "rgba(234, 179, 8, 0.15)",
                          borderRadius: "8px",
                          border: "2px solid #eab308",
                          textAlign: "center"
                        }}>
                          <div style={{ fontSize: "0.875rem", fontWeight: "700", color: "#eab308", marginBottom: "0.25rem" }}>
                            ⚡ ROYAL FLUSH DETECTED ⚡
                          </div>
                          <div style={{ fontSize: "0.75rem", color: "#334155" }}>
                            Perfect positioning + Strong sentiment
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* NEWS RISK WARNING - Always visible if HIGH/VERY HIGH */}
            {result.news_risk && (result.news_risk.news_risk === "VERY HIGH" || result.news_risk.news_risk === "HIGH") && (
              <div style={{ 
                marginTop: "1rem",
                padding: "1rem",
                background: "linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.15) 100%)",
                border: "3px solid #ef4444",
                borderRadius: "10px"
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "0.5rem" }}>
                  <div style={{ fontSize: "1.5rem" }}>⛔</div>
                  <div>
                    <div style={{ fontSize: "1.125rem", fontWeight: "700", color: "#dc2626" }}>
                      {result.news_risk.news_risk} NEWS RISK
                    </div>
                    <div style={{ fontSize: "0.875rem", fontWeight: "600", color: "#ef4444" }}>
                      {result.news_risk.recommendation}
                    </div>
                  </div>
                </div>
                <div style={{ 
                  padding: "0.75rem",
                  background: "rgba(255, 255, 255, 0.5)",
                  borderRadius: "6px",
                  fontSize: "0.75rem",
                  color: "#334155",
                  fontWeight: "500"
                }}>
                  ⚠️ {result.news_risk.note}
                  {result.news_risk.block_trading && (
                    <div style={{ marginTop: "0.5rem", color: "#dc2626", fontWeight: "700" }}>
                      🚫 Trading NOT recommended!
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* MULTI-TIMEFRAME ALIGNMENT - COLLAPSIBLE */}
            {result.timeframe_alignment && (
              <div style={{ marginTop: "1rem" }}>
                <button
                  onClick={() => toggleSection('timeframeAlignment')}
                  style={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "1rem",
                    background: result.timeframe_alignment.aligned
                      ? "rgba(34, 197, 94, 0.1)"
                      : "rgba(251, 191, 36, 0.1)",
                    border: `2px solid ${result.timeframe_alignment.aligned ? "#22c55e" : "#fbbf24"}`,
                    borderRadius: "10px",
                    cursor: "pointer",
                    transition: "all 0.3s ease"
                  }}
                >
                  <span style={{ fontSize: "1rem", fontWeight: "700", color: result.timeframe_alignment.aligned ? "#16a34a" : "#f59e0b" }}>
                    🎯 Timeframe Alignment ({result.timeframe_alignment.score}%)
                  </span>
                  <span style={{ fontSize: "1.25rem", color: result.timeframe_alignment.aligned ? "#16a34a" : "#f59e0b" }}>
                    {expandedSections.timeframeAlignment ? "−" : "+"}
                  </span>
                </button>
                
                {expandedSections.timeframeAlignment && (
                <div style={{ marginTop: "0.75rem", padding: "1rem", background: "rgba(34, 197, 94, 0.05)", borderRadius: "10px" }}>
                <div style={{
                  padding: "1.5rem",
                  background: result.timeframe_alignment.aligned
                    ? "linear-gradient(135deg, rgba(34, 197, 94, 0.1) 0%, rgba(22, 163, 74, 0.15) 100%)"
                    : "linear-gradient(135deg, rgba(251, 191, 36, 0.1) 0%, rgba(245, 158, 11, 0.15) 100%)",
                  border: `3px solid ${result.timeframe_alignment.aligned ? "#22c55e" : "#fbbf24"}`,
                  borderRadius: "12px"
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
                    <div style={{ fontSize: "1.75rem", fontWeight: "700", color: result.timeframe_alignment.aligned ? "#16a34a" : "#f59e0b" }}>
                      {result.timeframe_alignment.recommendation}
                    </div>
                    <div style={{
                      fontSize: "2.5rem",
                      fontWeight: "700",
                      color: result.timeframe_alignment.aligned ? "#16a34a" : "#f59e0b"
                    }}>
                      {result.timeframe_alignment.score}%
                    </div>
                  </div>
                  
                  {/* Timeframe Breakdown */}
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "1rem", marginTop: "1.5rem" }}>
                    {/* Daily */}
                    <div style={{
                      padding: "1rem",
                      background: "rgba(255, 255, 255, 0.5)",
                      borderRadius: "8px",
                      textAlign: "center"
                    }}>
                      <div style={{ fontSize: "0.75rem", color: "#64748b", fontWeight: "600", marginBottom: "0.5rem" }}>
                        DAILY
                      </div>
                      <div style={{
                        fontSize: "1.25rem",
                        fontWeight: "700",
                        color: result.timeframe_alignment.daily_trend === "BULLISH" ? "#16a34a" : result.timeframe_alignment.daily_trend === "BEARISH" ? "#dc2626" : "#64748b"
                      }}>
                        {result.timeframe_alignment.daily_trend === "BULLISH" && "📈 BULL"}
                        {result.timeframe_alignment.daily_trend === "BEARISH" && "📉 BEAR"}
                        {result.timeframe_alignment.daily_trend === "unknown" && "❓"}
                      </div>
                    </div>
                    
                    {/* 4-Hour */}
                    <div style={{
                      padding: "1rem",
                      background: "rgba(255, 255, 255, 0.5)",
                      borderRadius: "8px",
                      textAlign: "center"
                    }}>
                      <div style={{ fontSize: "0.75rem", color: "#64748b", fontWeight: "600", marginBottom: "0.5rem" }}>
                        4-HOUR
                      </div>
                      <div style={{
                        fontSize: "1.25rem",
                        fontWeight: "700",
                        color: result.timeframe_alignment.four_hour_trend === "BULLISH" ? "#16a34a" : result.timeframe_alignment.four_hour_trend === "BEARISH" ? "#dc2626" : "#64748b"
                      }}>
                        {result.timeframe_alignment.four_hour_trend === "BULLISH" && "📈 BULL"}
                        {result.timeframe_alignment.four_hour_trend === "BEARISH" && "📉 BEAR"}
                        {result.timeframe_alignment.four_hour_trend === "unknown" && "❓"}
                      </div>
                    </div>
                    
                    {/* 1-Hour */}
                    <div style={{
                      padding: "1rem",
                      background: "rgba(255, 255, 255, 0.5)",
                      borderRadius: "8px",
                      textAlign: "center"
                    }}>
                      <div style={{ fontSize: "0.75rem", color: "#64748b", fontWeight: "600", marginBottom: "0.5rem" }}>
                        1-HOUR
                      </div>
                      <div style={{
                        fontSize: "1.25rem",
                        fontWeight: "700",
                        color: result.timeframe_alignment.one_hour_trend === "BULLISH" ? "#16a34a" : result.timeframe_alignment.one_hour_trend === "BEARISH" ? "#dc2626" : "#64748b"
                      }}>
                        {result.timeframe_alignment.one_hour_trend === "BULLISH" && "📈 BULL"}
                        {result.timeframe_alignment.one_hour_trend === "BEARISH" && "📉 BEAR"}
                        {result.timeframe_alignment.one_hour_trend === "unknown" && "❓"}
                      </div>
                    </div>
                  </div>
                  
                  {/* Confidence Boost/Penalty */}
                  {result.timeframe_alignment.confidence_boost !== 0 && (
                    <div style={{
                      marginTop: "0.75rem",
                      padding: "0.75rem",
                      background: result.timeframe_alignment.confidence_boost > 0 ? "rgba(34, 197, 94, 0.1)" : "rgba(239, 68, 68, 0.1)",
                      borderRadius: "6px",
                      fontSize: "0.75rem",
                      fontWeight: "600",
                      color: result.timeframe_alignment.confidence_boost > 0 ? "#16a34a" : "#dc2626",
                      textAlign: "center"
                    }}>
                      {result.timeframe_alignment.confidence_boost > 0 ? "✅" : "⚠️"} Adjustment: {result.timeframe_alignment.confidence_boost > 0 ? "+" : ""}{result.timeframe_alignment.confidence_boost}%
                    </div>
                  )}
                </div>
                </div>
                )}
              </div>
            )}

            {/* PARTIAL TAKE PROFIT LEVELS - COLLAPSIBLE */}
            {result.partial_tp_levels && result.partial_tp_levels.length > 0 && (
              <div style={{ marginTop: "1rem" }}>
                <button
                  onClick={() => toggleSection('partialTP')}
                  style={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "1rem",
                    background: "rgba(59, 130, 246, 0.1)",
                    border: "2px solid #3b82f6",
                    borderRadius: "10px",
                    cursor: "pointer",
                    transition: "all 0.3s ease"
                  }}
                >
                  <span style={{ fontSize: "1rem", fontWeight: "700", color: "#3b82f6" }}>
                    💰 Partial Take Profit ({result.partial_tp_levels.length} levels)
                  </span>
                  <span style={{ fontSize: "1.25rem", color: "#3b82f6" }}>
                    {expandedSections.partialTP ? "−" : "+"}
                  </span>
                </button>
                
                {expandedSections.partialTP && (
                <div style={{ marginTop: "0.75rem", padding: "1rem", background: "rgba(59, 130, 246, 0.05)", borderRadius: "10px" }}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "1rem" }}>
                  {result.partial_tp_levels.map((level, index) => (
                    <div key={index} style={{
                      padding: "1.25rem",
                      background: "linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(37, 99, 235, 0.15) 100%)",
                      border: "2px solid #3b82f6",
                      borderRadius: "12px"
                    }}>
                      <div style={{ fontSize: "0.75rem", color: "#64748b", fontWeight: "600", marginBottom: "0.5rem" }}>
                        LEVEL {index + 1}
                      </div>
                      <div style={{ fontSize: "1.5rem", fontWeight: "700", color: "#3b82f6", marginBottom: "0.5rem" }}>
                        {level.price}
                      </div>
                      <div style={{ fontSize: "0.875rem", color: "#334155" }}>
                        Close <strong>{level.percent}%</strong> at <strong>{level.ratio}R</strong>
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{
                  marginTop: "0.75rem",
                  padding: "0.75rem",
                  background: "rgba(59, 130, 246, 0.1)",
                  borderRadius: "6px",
                  fontSize: "0.75rem",
                  color: "#334155"
                }}>
                  💡 Lock in gains while letting the rest run to full TP!
                </div>
                </div>
                )}
              </div>
            )}

            {/* FINAL PROBABILITY - Shown only if different from base */}
            {result.final_probability && result.final_probability !== result.probability && result.confidence_factors && result.confidence_factors.adjustments && (
              <div style={{ marginTop: "1rem" }}>
                <div style={{
                  padding: "1rem",
                  background: "linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(79, 70, 229, 0.15) 100%)",
                  border: "2px solid #6366f1",
                  borderRadius: "10px"
                }}>
                  <div style={{ fontSize: "0.875rem", color: "#64748b", marginBottom: "0.5rem" }}>
                    Probability Adjusted: {result.probability}% → <strong style={{ color: "#6366f1" }}>{result.final_probability}%</strong>
                  </div>
                  <div style={{ fontSize: "0.75rem", color: "#334155" }}>
                    {result.confidence_factors.adjustments.map((adj, i) => (
                      <span key={i} style={{ marginRight: "0.5rem" }}>
                        {adj.factor}: {adj.adjustment > 0 ? "+" : ""}{adj.adjustment}%
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* PRICE ACTION CONTEXT - COLLAPSIBLE */}
            {result.price_action && (
              <div style={{ marginTop: "1rem" }}>
                <button
                  onClick={() => toggleSection('priceAction')}
                  style={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "1rem",
                    background: "rgba(59, 130, 246, 0.1)",
                    border: "2px solid #3b82f6",
                    borderRadius: "10px",
                    cursor: "pointer",
                    transition: "all 0.3s ease"
                  }}
                >
                  <span style={{ fontSize: "1rem", fontWeight: "700", color: "#3b82f6" }}>
                    📋 Price Action Details
                  </span>
                  <span style={{ fontSize: "1.25rem", color: "#3b82f6" }}>
                    {expandedSections.priceAction ? "−" : "+"}
                  </span>
                </button>
                
                {expandedSections.priceAction && (
                <div style={{ marginTop: "0.75rem", padding: "1rem", background: "rgba(59, 130, 246, 0.05)", borderRadius: "10px" }}>
                <h3 style={{ fontSize: "0.875rem", fontWeight: "700", color: "#0f172a", marginBottom: "0.75rem" }}>
                  ⚡ Entry Timing & Order Type
                </h3>
                <div style={{
                  padding: "1.5rem",
                  background: "linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(79, 70, 229, 0.15) 100%)",
                  border: "3px solid #6366f1",
                  borderRadius: "12px"
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem" }}>
                    <div>
                      <div style={{ fontSize: "0.875rem", color: "#64748b", fontWeight: "600", marginBottom: "0.25rem" }}>
                        BASE PROBABILITY
                      </div>
                      <div style={{ fontSize: "2rem", fontWeight: "700", color: "#94a3b8" }}>
                        {result.probability}%
                      </div>
                    </div>
                    <div style={{ fontSize: "2rem", color: "#6366f1" }}>→</div>
                    <div>
                      <div style={{ fontSize: "0.875rem", color: "#64748b", fontWeight: "600", marginBottom: "0.25rem" }}>
                        FINAL PROBABILITY
                      </div>
                      <div style={{ fontSize: "2.5rem", fontWeight: "700", color: "#6366f1" }}>
                        {result.final_probability}%
                      </div>
                    </div>
                  </div>
                  
                  {/* Confidence Factors Breakdown */}
                  {result.confidence_factors && result.confidence_factors.adjustments && result.confidence_factors.adjustments.length > 0 && (
                    <div style={{ marginTop: "1rem" }}>
                      <div style={{ fontSize: "0.875rem", fontWeight: "600", color: "#64748b", marginBottom: "0.75rem" }}>
                        ADJUSTMENTS:
                      </div>
                      {result.confidence_factors.adjustments.map((adjustment, index) => (
                        <div key={index} style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "0.75rem",
                          background: "rgba(255, 255, 255, 0.5)",
                          borderRadius: "8px",
                          marginBottom: "0.5rem",
                          fontSize: "0.875rem"
                        }}>
                          <div style={{ color: "#334155" }}>
                            <strong>{adjustment.factor}:</strong> {adjustment.reason}
                          </div>
                          <div style={{
                            fontWeight: "700",
                            color: adjustment.adjustment > 0 ? "#16a34a" : "#dc2626"
                          }}>
                            {adjustment.adjustment > 0 ? "+" : ""}{adjustment.adjustment}%
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Price Action Context & Trade Type Recommendation */}
            {result.price_action && (
              <div style={{ marginTop: "2rem" }}>
                <h3 style={{ fontSize: "1.125rem", fontWeight: "700", color: "#0f172a", marginBottom: "1rem" }}>
                  ⚡ Entry Timing & Order Type
                </h3>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "1.5rem" }}>
                  
                  {/* Price Action Context */}
                  <div style={{
                    background: result.price_action.context.includes("Enter Now")
                      ? "rgba(34, 197, 94, 0.1)"
                      : result.price_action.context.includes("Set Limit") || result.price_action.context === "Neutral Zone"
                      ? "rgba(59, 130, 246, 0.1)"
                      : result.price_action.context === "Overextended"
                      ? "rgba(239, 68, 68, 0.1)"
                      : result.price_action.context === "Pulling Back"
                      ? "rgba(251, 191, 36, 0.1)"
                      : "rgba(148, 163, 184, 0.1)",
                    padding: "1.5rem",
                    borderRadius: "12px",
                    border: `2px solid ${
                      result.price_action.context.includes("Enter Now")
                        ? "#22c55e"
                        : result.price_action.context.includes("Set Limit") || result.price_action.context === "Neutral Zone"
                        ? "#3b82f6"
                        : result.price_action.context === "Overextended"
                        ? "#ef4444"
                        : result.price_action.context === "Pulling Back"
                        ? "#fbbf24"
                        : "#94a3b8"
                    }`
                  }}>
                    <div style={{ fontSize: "0.875rem", fontWeight: "600", color: "#64748b", marginBottom: "0.5rem" }}>
                      Price Action Context
                    </div>
                    <div style={{ 
                      fontSize: "1.5rem", 
                      fontWeight: "700",
                      color: result.price_action.context.includes("Enter Now")
                        ? "#22c55e"
                        : result.price_action.context.includes("Set Limit") || result.price_action.context === "Neutral Zone"
                        ? "#3b82f6"
                        : result.price_action.context === "Overextended"
                        ? "#ef4444"
                        : result.price_action.context === "Pulling Back"
                        ? "#fbbf24"
                        : "#64748b",
                      marginBottom: "0.5rem"
                    }}>
                      {result.price_action.context.includes("At Support") && "📊 At Support"}
                      {result.price_action.context.includes("At Resistance") && "📊 At Resistance"}
                      {result.price_action.context.includes("Near Support") && "📊 Near Support"}
                      {result.price_action.context.includes("Near Resistance") && "📊 Near Resistance"}
                      {result.price_action.context.includes("Breaking Out") && "🚀 Breaking Out"}
                      {result.price_action.context.includes("Breaking Down") && "📉 Breaking Down"}
                      {result.price_action.context === "Overextended" && "⚠️ Overextended"}
                      {result.price_action.context === "Pulling Back" && "↩️ Pulling Back"}
                      {result.price_action.context === "Neutral Zone" && "➡️ Neutral Zone"}
                      {result.price_action.context === "Unknown" && "❓ Unknown"}
                    </div>
                    <div style={{ fontSize: "0.75rem", color: "#334155", marginTop: "0.5rem" }}>
                      {result.price_action.context.includes("Enter Now") && "✅ Perfect setup - High confidence entry"}
                      {result.price_action.context.includes("Set Limit") && "🎯 Set limit order to catch the move"}
                      {result.price_action.context === "Overextended" && "⏳ Don't chase - Wait for pullback"}
                      {result.price_action.context === "Pulling Back" && "⏳ Wait - Price retracing"}
                      {result.price_action.context === "Neutral Zone" && "Standard entry - Patience recommended"}
                    </div>
                  </div>

                  {/* Entry Action Recommendation */}
                  <div style={{
                    background: result.price_action.context.includes("Enter Now")
                      ? "rgba(34, 197, 94, 0.1)"
                      : result.price_action.context === "Overextended" || result.price_action.context === "Pulling Back"
                      ? "rgba(251, 191, 36, 0.1)"
                      : "rgba(59, 130, 246, 0.1)",
                    padding: "1.5rem",
                    borderRadius: "12px",
                    border: `3px solid ${
                      result.price_action.context.includes("Enter Now")
                        ? "#22c55e"
                        : result.price_action.context === "Overextended" || result.price_action.context === "Pulling Back"
                        ? "#fbbf24"
                        : "#3b82f6"
                    }`
                  }}>
                    <div style={{ fontSize: "0.875rem", fontWeight: "600", color: "#64748b", marginBottom: "0.5rem" }}>
                      Entry Action
                    </div>
                    <div style={{ 
                      fontSize: "1.75rem", 
                      fontWeight: "700",
                      color: result.price_action.context.includes("Enter Now")
                        ? "#16a34a"
                        : result.price_action.context === "Overextended" || result.price_action.context === "Pulling Back"
                        ? "#f59e0b"
                        : "#3b82f6",
                      marginBottom: "0.75rem"
                    }}>
                      {result.price_action.context.includes("Enter Now") && result.price_action.context.includes("Breaking") ? "🚀 ENTER NOW" : ""}
                      {result.price_action.context.includes("Enter Now") && !result.price_action.context.includes("Breaking") ? "✅ ENTER NOW" : ""}
                      {result.price_action.context.includes("Set Limit") ? "🎯 SET LIMIT" : ""}
                      {result.price_action.context === "Overextended" ? "⏳ WAIT" : ""}
                      {result.price_action.context === "Pulling Back" ? "⏳ WAIT" : ""}
                      {result.price_action.context === "Neutral Zone" ? "📋 SET LIMIT" : ""}
                      {result.price_action.context === "Unknown" ? "⚠️ CAUTION" : ""}
                    </div>
                    <div style={{ 
                      fontSize: "1.125rem", 
                      fontWeight: "600",
                      color: "#334155",
                      marginBottom: "0.75rem"
                    }}>
                      Order: {result.price_action.recommendation}
                    </div>
                    <div style={{ fontSize: "0.875rem", color: "#334155", lineHeight: "1.5", marginBottom: "1rem" }}>
                      {result.price_action.reason}
                    </div>
                    <div style={{ 
                      padding: "0.75rem", 
                      background: "rgba(0, 0, 0, 0.05)", 
                      borderRadius: "8px",
                      fontSize: "0.875rem",
                      color: "#334155"
                    }}>
                      <strong>Entry Confidence:</strong> {result.price_action.confidence}%
                    </div>
                    
                    {/* Counter-Trade Scalping Suggestion - Only for Neutral Zone */}
                    {result.price_action.counter_trade_suggestion && (
                      <div style={{
                        marginTop: "1rem",
                        padding: "1rem",
                        background: "linear-gradient(135deg, rgba(234, 179, 8, 0.1) 0%, rgba(251, 191, 36, 0.15) 100%)",
                        border: "2px solid #eab308",
                        borderRadius: "10px"
                      }}>
                        <div style={{ 
                          fontSize: "0.875rem", 
                          fontWeight: "700", 
                          color: "#eab308",
                          marginBottom: "0.5rem",
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem"
                        }}>
                          💰 BONUS SCALPING OPPORTUNITY
                        </div>
                        <div style={{ 
                          fontSize: "0.875rem", 
                          color: "#334155",
                          lineHeight: "1.5",
                          marginBottom: "0.75rem"
                        }}>
                          {result.price_action.counter_trade_suggestion.message}
                        </div>
                        <div style={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr 1fr",
                          gap: "0.5rem",
                          fontSize: "0.75rem"
                        }}>
                          <div style={{ 
                            padding: "0.5rem",
                            background: "rgba(255, 255, 255, 0.7)",
                            borderRadius: "6px",
                            textAlign: "center"
                          }}>
                            <div style={{ color: "#64748b", marginBottom: "0.25rem" }}>Action</div>
                            <div style={{ fontWeight: "700", color: "#0f172a" }}>
                              {result.price_action.counter_trade_suggestion.action}
                            </div>
                          </div>
                          <div style={{ 
                            padding: "0.5rem",
                            background: "rgba(255, 255, 255, 0.7)",
                            borderRadius: "6px",
                            textAlign: "center"
                          }}>
                            <div style={{ color: "#64748b", marginBottom: "0.25rem" }}>Entry</div>
                            <div style={{ fontWeight: "700", color: "#0f172a" }}>
                              {result.price_action.counter_trade_suggestion.entry.toFixed(5)}
                            </div>
                          </div>
                          <div style={{ 
                            padding: "0.5rem",
                            background: "rgba(255, 255, 255, 0.7)",
                            borderRadius: "6px",
                            textAlign: "center"
                          }}>
                            <div style={{ color: "#64748b", marginBottom: "0.25rem" }}>TP</div>
                            <div style={{ fontWeight: "700", color: "#22c55e" }}>
                              {result.price_action.counter_trade_suggestion.tp.toFixed(5)}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                </div>
                </div>
                )}
              </div>
            )}

            {/* HISTORICAL PERFORMANCE SECTION */}
            {result.historical_performance && (
              <div style={{
                background: result.historical_performance.warning 
                  ? "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)"
                  : result.historical_performance.win_rate >= 70
                  ? "linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)"
                  : "linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%)",
                borderRadius: "16px",
                padding: "1.5rem",
                marginBottom: "1.5rem",
                border: result.historical_performance.warning ? "2px solid #f59e0b" : "2px solid #e5e7eb"
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "1rem" }}>
                  <span style={{ fontSize: "1.5rem" }}>
                    {result.historical_performance.win_rate === null ? "📊" : result.historical_performance.warning ? "⚠️" : "✅"}
                  </span>
                  <h3 style={{ fontSize: "1.1rem", fontWeight: "700", color: "#1e293b", margin: 0 }}>
                    TradeBlindfolded Historical Performance
                  </h3>
                </div>

                {result.historical_performance.win_rate !== null ? (
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: "2rem", marginBottom: "1rem" }}>
                      <div>
                        <div style={{ fontSize: "0.875rem", color: "#64748b", fontWeight: "600", marginBottom: "0.25rem" }}>
                          WIN RATE FOR THIS SETUP
                        </div>
                        <div style={{ 
                          fontSize: "3rem", 
                          fontWeight: "700",
                          color: result.historical_performance.warning ? "#f59e0b" : "#10b981"
                        }}>
                          {result.historical_performance.win_rate}%
                        </div>
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: "0.875rem", color: "#64748b", marginBottom: "0.5rem" }}>
                          <strong>Setup:</strong> {result.pair} {result.direction} on {result.timeframe || '1D'} timeframe
                        </div>
                        <div style={{ fontSize: "0.875rem", color: "#64748b", marginBottom: "0.5rem" }}>
                          <strong>Sample Size:</strong> {result.historical_performance.sample_size} completed trades
                        </div>
                        <div style={{ fontSize: "0.875rem", color: "#475569", fontWeight: "600", marginTop: "0.75rem" }}>
                          {result.historical_performance.message}
                        </div>
                      </div>
                    </div>

                    {result.historical_performance.warning && (
                      <div style={{
                        background: "rgba(245, 158, 11, 0.15)",
                        border: "2px solid #f59e0b",
                        borderRadius: "8px",
                        padding: "1rem",
                        marginTop: "1rem"
                      }}>
                        <div style={{ display: "flex", alignItems: "start", gap: "0.75rem" }}>
                          <span style={{ fontSize: "1.5rem" }}>⚠️</span>
                          <div>
                            <div style={{ fontWeight: "700", color: "#92400e", marginBottom: "0.25rem" }}>
                              Low Historical Performance
                            </div>
                            <div style={{ fontSize: "0.875rem", color: "#78350f" }}>
                              This setup has underperformed historically. Consider waiting for better market conditions or higher confidence signals.
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {result.historical_performance.win_rate >= 70 && (
                      <div style={{
                        background: "rgba(16, 185, 129, 0.15)",
                        border: "2px solid #10b981",
                        borderRadius: "8px",
                        padding: "1rem",
                        marginTop: "1rem"
                      }}>
                        <div style={{ display: "flex", alignItems: "start", gap: "0.75rem" }}>
                          <span style={{ fontSize: "1.5rem" }}>✅</span>
                          <div>
                            <div style={{ fontWeight: "700", color: "#065f46", marginBottom: "0.25rem" }}>
                              Strong Historical Performance
                            </div>
                            <div style={{ fontSize: "0.875rem", color: "#047857" }}>
                              This setup has performed well historically. Both market analysis and track record align!
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ 
                    padding: "1rem",
                    background: "rgba(148, 163, 184, 0.1)",
                    borderRadius: "8px",
                    fontSize: "0.875rem",
                    color: "#475569"
                  }}>
                    <strong>No historical data available yet.</strong> This is a new setup. Your trade will be tracked and contribute to future performance data.
                  </div>
                )}
              </div>
            )}

            <div className="analysis-summary" style={{ marginTop: volumeAnalysis ? "1.5rem" : "0" }}>
              <p data-testid="analysis-summary">{result.analysis_summary}</p>
              {enableAutoSL && trailingStops.length > 0 && (
                <div style={{ marginTop: "1rem" }}>
                  <p style={{ color: "#10b981", fontWeight: "600", marginBottom: "0.75rem" }}>
                    ✓ Trailing Stop Loss configured with {trailingStops.length} milestone{trailingStops.length > 1 ? 's' : ''}
                  </p>
                  {!monitoringActive ? (
                    <button
                      onClick={handleActivateMonitoring}
                      style={{
                        padding: "0.875rem 1.5rem",
                        background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                        border: "none",
                        borderRadius: "8px",
                        color: "white",
                        fontSize: "1rem",
                        fontWeight: "700",
                        cursor: "pointer",
                        boxShadow: "0 4px 15px rgba(16, 185, 129, 0.3)",
                        transition: "all 0.3s ease",
                        fontFamily: "'Space Grotesk', sans-serif"
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.transform = "translateY(-2px)";
                        e.target.style.boxShadow = "0 8px 25px rgba(16, 185, 129, 0.4)";
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = "translateY(0)";
                        e.target.style.boxShadow = "0 4px 15px rgba(16, 185, 129, 0.3)";
                      }}
                      data-testid="activate-monitoring-btn"
                    >
                      🤖 Activate Automated Monitoring
                    </button>
                  ) : (
                    <div style={{
                      padding: "1rem",
                      background: "rgba(16, 185, 129, 0.1)",
                      border: "2px solid #10b981",
                      borderRadius: "8px",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center"
                    }}>
                      <div>
                        <div style={{ color: "#10b981", fontWeight: "700", marginBottom: "0.25rem" }}>
                          🤖 Automated Monitoring Active
                        </div>
                        <div style={{ color: "#334155", fontSize: "0.875rem" }}>
                          Stop loss will adjust automatically every 2 minutes
                        </div>
                      </div>
                      <button
                        onClick={handleDeactivateMonitoring}
                        style={{
                          padding: "0.5rem 1rem",
                          background: "rgba(239, 68, 68, 0.1)",
                          border: "2px solid #ef4444",
                          borderRadius: "6px",
                          color: "#b91c1c",
                          fontSize: "0.875rem",
                          fontWeight: "600",
                          cursor: "pointer"
                        }}
                        data-testid="deactivate-monitoring-btn"
                      >
                        Stop Monitoring
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {mt5Connected && (
              <div className="mt5-actions">
                <button className="mt5-btn" onClick={handlePlaceTrade} data-testid="place-trade-btn">
                  Place Trade on MT5 (Auto)
                </button>
                <button className="mt5-btn secondary" data-testid="mt5-account-info">
                  Account: {mt5Account?.login} | ${mt5Account?.balance}
                </button>
              </div>
            )}

            <div className="mt5-actions" style={{ marginTop: mt5Connected ? "0.75rem" : "0" }}>
              <button 
                className="mt5-btn" 
                onClick={() => setShowTradeModal(true)}
                style={{ background: "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)" }}
                data-testid="set-trade-btn"
              >
                Set Trade in MT5 App
              </button>
              {!mt5Connected && (
                <Link to="/mt5" className="mt5-btn secondary" data-testid="connect-mt5-link">
                  Connect MT5 for Auto-Trade
                </Link>
              )}
            </div>
          
            <div className="nav-links">
              <Link to="/history" className="nav-link" data-testid="history-link">View Trade History</Link>
              <Link to="/mt5" className="nav-link" data-testid="mt5-link">MT5 Connection</Link>
            </div>
          </div>
        </>
      )}

      {/* Trade Setup Modal */}
      {showTradeModal && result && (
        <div 
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.7)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            padding: "1rem"
          }}
          onClick={() => setShowTradeModal(false)}
        >
          <div 
            style={{
              background: "linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)",
              borderRadius: "16px",
              padding: "2rem",
              maxWidth: "600px",
              width: "100%",
              maxHeight: "90vh",
              overflowY: "auto",
              boxShadow: "0 20px 60px rgba(8, 145, 178, 0.3)"
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem" }}>
              <h2 style={{ fontSize: "1.5rem", fontWeight: "700", color: "#0f172a" }}>Setup Trade in MT5</h2>
              <button
                onClick={() => setShowTradeModal(false)}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "1.5rem",
                  cursor: "pointer",
                  color: "#64748b"
                }}
              >
                ✕
              </button>
            </div>

            <div style={{ background: "rgba(139, 92, 246, 0.1)", padding: "1rem", borderRadius: "12px", marginBottom: "1.5rem", border: "1px solid rgba(139, 92, 246, 0.3)" }}>
              <p style={{ color: "#7c3aed", fontWeight: "600", fontSize: "0.875rem", marginBottom: "0.5rem" }}>
                📱 Opening MT5 App...
              </p>
              <p style={{ color: "#64748b", fontSize: "0.875rem" }}>
                Trade details copied to clipboard. Follow the steps below to set up your trade.
              </p>
            </div>

            <div style={{ background: "white", padding: "1.5rem", borderRadius: "12px", marginBottom: "1.5rem" }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem", marginBottom: "1rem" }}>
                <div>
                  <div style={{ fontSize: "0.75rem", color: "#64748b", marginBottom: "0.25rem" }}>Trading Pair</div>
                  <div style={{ fontSize: "1.25rem", fontWeight: "700", color: "#0f172a" }}>{result.pair}</div>
                </div>
                <div>
                  <div style={{ fontSize: "0.75rem", color: "#64748b", marginBottom: "0.25rem" }}>Direction</div>
                  <div style={{ fontSize: "1.25rem", fontWeight: "700", color: result.direction === "BUY" ? "#10b981" : "#ef4444" }}>
                    {result.direction}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: "0.75rem", color: "#64748b", marginBottom: "0.25rem" }}>Lot Size</div>
                  <div style={{ fontSize: "1.25rem", fontWeight: "700", color: "#0f172a" }}>{result.lot_size}</div>
                </div>
                <div>
                  <div style={{ fontSize: "0.75rem", color: "#64748b", marginBottom: "0.25rem" }}>Entry Price</div>
                  <div style={{ fontSize: "1.25rem", fontWeight: "700", color: "#0f172a" }}>{result.entry_price}</div>
                </div>
                <div>
                  <div style={{ fontSize: "0.75rem", color: "#64748b", marginBottom: "0.25rem" }}>Stop Loss</div>
                  <div style={{ fontSize: "1.25rem", fontWeight: "700", color: "#ef4444" }}>
                    {result.stop_loss}
                    {calculateHalfSL(result.entry_price, result.stop_loss, result.pair) && (
                      <span style={{ fontSize: "0.9rem", color: "#f97316", marginLeft: "0.25rem" }}>
                        ({calculateHalfSL(result.entry_price, result.stop_loss, result.pair)})
                      </span>
                    )}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: "0.75rem", color: "#64748b", marginBottom: "0.25rem" }}>Take Profit</div>
                  <div style={{ fontSize: "1.25rem", fontWeight: "700", color: "#10b981" }}>{result.take_profit}</div>
                </div>
              </div>
            </div>

            {/* Trade Statistics Section */}
            {result.trade_statistics && result.trade_statistics.sample_size > 0 && (
              <div style={{ 
                background: "linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)", 
                padding: "1.5rem", 
                borderRadius: "12px", 
                marginBottom: "1.5rem",
                border: "2px solid #3b82f6"
              }}>
                <h3 style={{ fontSize: "1.125rem", fontWeight: "700", color: "#1e40af", marginBottom: "1rem", display: "flex", alignItems: "center", gap: "0.5rem" }}>
                  📊 Historical Performance for {result.pair} {result.direction}
                </h3>
                
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "1rem", marginBottom: "1rem" }}>
                  <div style={{ background: "rgba(255, 255, 255, 0.8)", padding: "1rem", borderRadius: "8px" }}>
                    <div style={{ fontSize: "0.75rem", color: "#64748b", marginBottom: "0.25rem" }}>Market Analysis</div>
                    <div style={{ fontSize: "1.5rem", fontWeight: "700", color: "#1e40af" }}>
                      {result.probability}% {result.direction}
                    </div>
                  </div>
                  
                  <div style={{ background: "rgba(255, 255, 255, 0.8)", padding: "1rem", borderRadius: "8px" }}>
                    <div style={{ fontSize: "0.75rem", color: "#64748b", marginBottom: "0.25rem" }}>TradeBlindfolded Win Rate</div>
                    <div style={{ fontSize: "1.5rem", fontWeight: "700", color: result.trade_statistics.historical_win_rate >= 70 ? "#10b981" : result.trade_statistics.historical_win_rate >= 50 ? "#f59e0b" : "#ef4444" }}>
                      {result.trade_statistics.historical_win_rate}%
                    </div>
                    <div style={{ fontSize: "0.7rem", color: "#64748b", marginTop: "0.25rem" }}>
                      {result.trade_statistics.won_count} wins / {result.trade_statistics.sample_size} trades
                    </div>
                  </div>
                  
                  {result.trade_statistics.trailing_sl_total_captured > 0 && (
                    <div style={{ background: "rgba(255, 255, 255, 0.8)", padding: "1rem", borderRadius: "8px" }}>
                      <div style={{ fontSize: "0.75rem", color: "#64748b", marginBottom: "0.25rem" }}>Secured w/ Auto Trailing SL</div>
                      <div style={{ fontSize: "1.5rem", fontWeight: "700", color: "#10b981" }}>
                        ${result.trade_statistics.trailing_sl_total_captured.toLocaleString()}
                      </div>
                      <div style={{ fontSize: "0.7rem", color: "#64748b", marginTop: "0.25rem" }}>
                        From {result.trade_statistics.lost_count} losing trades
                      </div>
                    </div>
                  )}
                  
                  <div style={{ background: "rgba(255, 255, 255, 0.8)", padding: "1rem", borderRadius: "8px" }}>
                    <div style={{ fontSize: "0.75rem", color: "#64748b", marginBottom: "0.25rem" }}>TP's Reached (3:1 R:R)</div>
                    <div style={{ fontSize: "1.5rem", fontWeight: "700", color: "#1e40af" }}>
                      {result.trade_statistics.tp_reached_count} of {result.trade_statistics.sample_size}
                    </div>
                    <div style={{ fontSize: "0.7rem", color: "#64748b", marginTop: "0.25rem" }}>
                      {result.trade_statistics.sample_size > 0 ? Math.round((result.trade_statistics.tp_reached_count / result.trade_statistics.sample_size) * 100) : 0}% hit full TP
                    </div>
                  </div>
                </div>
                
                <div style={{ fontSize: "0.875rem", color: "#1e40af", fontStyle: "italic", textAlign: "center" }}>
                  {result.trade_statistics.message}
                </div>
              </div>
            )}

            <div style={{ background: "white", padding: "1.5rem", borderRadius: "12px", marginBottom: "1.5rem" }}>
              <h3 style={{ fontSize: "1rem", fontWeight: "600", color: "#0f172a", marginBottom: "1rem" }}>
                📋 Setup Instructions:
              </h3>
              <ol style={{ paddingLeft: "1.25rem", color: "#334155", lineHeight: "1.8", fontSize: "0.875rem" }}>
                <li>Open <strong>MetaTrader 5</strong> app on your device</li>
                <li>Search for <strong>{result.pair}</strong> in Quotes</li>
                <li>Tap the pair and select <strong>"Trade"</strong></li>
                <li><strong>Choose order type:</strong> Market Execution, Buy/Sell Limit, Buy/Sell Stop</li>
                <li>Set <strong>Volume: {result.lot_size}</strong></li>
                <li>Set <strong>Stop Loss: {result.stop_loss}</strong></li>
                <li>Set <strong>Take Profit: {result.take_profit}</strong></li>
                <li>Select <strong>{result.direction}</strong> direction</li>
                <li>Review and <strong>confirm</strong> your trade</li>
              </ol>
            </div>

            <div style={{ display: "flex", gap: "0.75rem" }}>
              <button
                onClick={handleSetTrade}
                style={{
                  flex: 1,
                  padding: "1rem",
                  background: "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
                  border: "none",
                  borderRadius: "12px",
                  color: "white",
                  fontSize: "1rem",
                  fontWeight: "600",
                  cursor: "pointer"
                }}
              >
                Open MT5 & Copy Details
              </button>
              <button
                onClick={() => setShowTradeModal(false)}
                style={{
                  padding: "1rem 1.5rem",
                  background: "rgba(148, 163, 184, 0.2)",
                  border: "2px solid rgba(148, 163, 184, 0.5)",
                  borderRadius: "12px",
                  color: "#334155",
                  fontSize: "1rem",
                  fontWeight: "600",
                  cursor: "pointer"
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Subscription Required Modal for MT5 Feature */}
      {showSubscriptionModal && (
        <div 
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.75)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10000,
            padding: "1rem"
          }}
          onClick={() => setShowSubscriptionModal(false)}
        >
          <div 
            style={{
              background: "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)",
              borderRadius: "20px",
              padding: "2.5rem",
              maxWidth: "500px",
              width: "100%",
              boxShadow: "0 25px 70px rgba(245, 158, 11, 0.4)",
              border: "3px solid #f59e0b",
              textAlign: "center"
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Icon */}
            <div style={{
              fontSize: "4rem",
              marginBottom: "1rem"
            }}>
              🔒
            </div>

            {/* Title */}
            <h2 style={{
              fontSize: "1.75rem",
              fontWeight: "700",
              color: "#92400e",
              marginBottom: "1rem"
            }}>
              Premium Feature
            </h2>

            {/* Message */}
            <p style={{
              fontSize: "1.125rem",
              color: "#78350f",
              marginBottom: "2rem",
              lineHeight: "1.6"
            }}>
              Purchase subscription to access this feature
            </p>

            {/* Buttons */}
            <div style={{ display: "flex", gap: "1rem", flexDirection: "column" }}>
              <button
                onClick={() => {
                  setShowSubscriptionModal(false);
                  // Navigate to profile/subscription page
                  window.location.href = "#profile";
                }}
                style={{
                  padding: "1rem 2rem",
                  background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                  border: "none",
                  borderRadius: "12px",
                  color: "white",
                  fontSize: "1.125rem",
                  fontWeight: "700",
                  cursor: "pointer",
                  boxShadow: "0 4px 15px rgba(245, 158, 11, 0.3)",
                  transition: "all 0.3s ease"
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = "translateY(-2px)";
                  e.target.style.boxShadow = "0 6px 20px rgba(245, 158, 11, 0.4)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = "translateY(0)";
                  e.target.style.boxShadow = "0 4px 15px rgba(245, 158, 11, 0.3)";
                }}
              >
                View Subscription Plans
              </button>

              <button
                onClick={() => setShowSubscriptionModal(false)}
                style={{
                  padding: "0.875rem 2rem",
                  background: "transparent",
                  border: "2px solid #d97706",
                  borderRadius: "12px",
                  color: "#92400e",
                  fontSize: "1rem",
                  fontWeight: "600",
                  cursor: "pointer",
                  transition: "all 0.3s ease"
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = "rgba(217, 119, 6, 0.1)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = "transparent";
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Subscription Required Modal */}
      {showSubscriptionModal && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(0, 0, 0, 0.7)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 10000,
          padding: "1rem"
        }}>
          <div style={{
            background: "white",
            borderRadius: "16px",
            padding: "2.5rem",
            maxWidth: "500px",
            width: "100%",
            boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
            textAlign: "center"
          }}>
            {/* Warning Icon */}
            <div style={{
              fontSize: "4rem",
              marginBottom: "1rem"
            }}>
              ⚠️
            </div>

            {/* Message */}
            <h2 style={{
              fontSize: "1.5rem",
              fontWeight: "700",
              color: "#0f172a",
              marginBottom: "1rem"
            }}>
              Subscription Required
            </h2>
            
            <p style={{
              fontSize: "1rem",
              color: "#64748b",
              lineHeight: "1.6",
              marginBottom: "2rem"
            }}>
              {subscriptionModalMessage}
            </p>

            {/* Buttons */}
            <div style={{
              display: "flex",
              gap: "1rem",
              justifyContent: "center",
              flexWrap: "wrap"
            }}>
              <a
                href="/upgrade"
                style={{
                  padding: "1rem 2rem",
                  background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: "1rem",
                  fontWeight: "700",
                  cursor: "pointer",
                  textDecoration: "none",
                  display: "inline-block",
                  transition: "all 0.3s ease"
                }}
                onMouseEnter={(e) => e.target.style.transform = "translateY(-2px)"}
                onMouseLeave={(e) => e.target.style.transform = "translateY(0)"}
              >
                Subscribe Now
              </a>
              
              <button
                onClick={() => setShowSubscriptionModal(false)}
                style={{
                  padding: "1rem 2rem",
                  background: "transparent",
                  color: "#64748b",
                  border: "2px solid #e2e8f0",
                  borderRadius: "8px",
                  fontSize: "1rem",
                  fontWeight: "600",
                  cursor: "pointer",
                  transition: "all 0.3s ease"
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = "#f8fafc";
                  e.target.style.borderColor = "#cbd5e1";
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = "transparent";
                  e.target.style.borderColor = "#e2e8f0";
                }}
              >
                Maybe Later
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ForexCalculator;